import React, { useId, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Copy, Download, Check, RotateCw, BarChart3, Clock, Zap, Bookmark, Loader2, ChevronDown, BookOpen, AlertTriangle, ShieldCheck } from 'lucide-react';
import { ThinkingPanel } from './thinking/ThinkingPanel';
import { CmaProgressLines, buildCmaProgressLines } from './thinking/CmaProgressLines';
import {
  FinalResponseRenderer,
  injectCodeMarkers,
  ClinicalQualityReviewPanel,
  clinicalQualityVisible,
  ResponseFrame,
} from './response';
import type { CodeRef } from './response/CodeReferencesPanel';
import { CodeReferencesUiContext } from './response/codeReferencesContext';
import { DraftAdvisoryStrip, hasDraftArtifactTools } from './response/DraftBadge';
import { MARKDOWN_PROSE_CLASS, CHART_BRIEFING_PROSE_CLASS, ClinicalMarkdownBody } from './response/clinicalMarkdownComponents';
import type { StructuredResponse } from './response/types';
import {
  hasUsefulStructuredContent,
  structuredPayloadForChartBriefingView,
  widgetsForChartBriefingView,
} from './response/contentUtils';
import { splitLensGroupBranches } from './response/ClinicalLensGroupCards';
import { escapeHtmlWithBreaks, rawHtml, safeHtml } from '../utils/htmlExport';
import {
  computeMetrics,
  deriveExecutionStatus,
} from './thinking/normalizeThinkingEvent';
import type { NormalizedThinkingEvent } from './thinking/types';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { ChartBriefingFollowUpChips } from './ChartBriefingFollowUpChips';
import { shouldRenderChartBriefingFollowUpChips } from '../lib/chartBriefingFollowUpActions';

type RagSpanPayload = { start: number; end: number; resource: string };

type RagSourcePayload = {
  source: string;
  number?: string;
  content_preview?: string;
  resource?: string;
  chunk_id?: string;
  document_id?: string;
  score?: string;
};

// Option A CMA per-branch RAG: the guideline_evidence contract (backend
// control_plane meta["guideline_evidence"]). Powers the citation-free
// reference list + hover tooltip and the per-category answer area.
type GuidelineEvidencePassage = {
  category?: string | null;
  number?: string | null;
  snippet?: string | null;
  passage_text?: string | null;
  document_type?: string | null;
  section?: string | null;
  page_start?: number | string | null;
  page_end?: number | string | null;
};

type GuidelineEvidenceReference = {
  doc_title?: string | null;
  document_type?: string | null;
  passages?: GuidelineEvidencePassage[];
};

type GuidelineEvidenceCategory = {
  category?: string | null;
  answer?: string | null;
  evidence_quality?: string | null;
  source_numbers?: Array<string | number | null>;
};

type GuidelineEvidencePayload = {
  categories?: GuidelineEvidenceCategory[];
  references?: GuidelineEvidenceReference[];
  // #75 (Kira round): true when the specialist review produced no modeled
  // opinion — evidence was retrieved but nothing cites it.
  review_incomplete?: boolean;
  // #44's retrieval-quality verdict. Still computed by the backend and still
  // carried on this payload, but DELIBERATELY NOT RENDERED (USER ORDER
  // 2026-08-10, rule 35): the user does not want the "evidence note" chip or
  // the disclosure line in the Clinical references card. The field stays on
  // the type because the backend still sends it and the debug export still
  // reads it — deleting the type would only hide it from whoever looks next.
  retrieval_quality?: {
    weak_categories?: string[];
    empty_answer_categories?: string[];
    disclosure?: string | null;
  };
};

type SpecialtyResultPayload = {
  specialty?: string;
  summary?: string;
  opinion?: string;
  supporting_rationale?: string;
  grounded_claims?: string[];
  recommended_followup_questions?: string[];
  recommended_additional_data?: string[];
  missing_evidence?: string[];
  limitations?: string[];
  metadata?: {
    rag_sources?: RagSourcePayload[];
  };
  references?: RagSourcePayload[];
};

/** Hide the references panel for empty/legacy placeholder rows (no real excerpt or document). */
function isSubstantiveClinicalReference(s: RagSourcePayload): boolean {
  const preview = (s.content_preview ?? '').trim();
  const src = (s.source ?? '').trim();
  const res = (s.resource ?? '').trim();
  const hasDoc =
    (s.document_id && String(s.document_id).length > 4) ||
    (s.chunk_id && String(s.chunk_id).length > 4);
  if (hasDoc) return true;
  if (preview.length >= 40) return true;
  // Exact matches against our own emitted legacy placeholder constants only
  // (closed system labels) — no fuzzy substring matching.
  const legacySynthetic =
    src === 'Knowledge base (RAG)' || res === 'Consolidated RAG response';
  if (legacySynthetic && preview.length < 40) return false;
  if (
    preview === 'Retrieved guidance (see evidence in model context).' ||
    preview === 'Retrieved guidance (see evidence in model context)'
  ) {
    return false;
  }
  return preview.length > 0 || src.length > 0 || res.length > 0;
}

function filterClinicalReferenceSources(sources: RagSourcePayload[]): RagSourcePayload[] {
  return sources.filter(isSubstantiveClinicalReference);
}

// #18: walks every text-bearing field of a structured response so the
// citation-marker check below sees the same content the clinician actually
// reads (table cells never carry citation prose, so they're intentionally
// skipped). Structural tree-walk, not a keyword scan.
function collectStructuredSectionText(sections: unknown): string {
  if (!Array.isArray(sections)) return '';
  const parts: string[] = [];
  const pushNestedItems = (items: unknown[]): void => {
    for (const entry of items) {
      if (entry == null) continue;
      if (typeof entry !== 'object') {
        parts.push(String(entry));
        continue;
      }
      const e = entry as { title?: unknown; children?: unknown; groups?: unknown };
      if (e.title != null) parts.push(String(e.title));
      if (Array.isArray(e.children)) parts.push(...e.children.map((c) => String(c ?? '')));
      if (Array.isArray(e.groups)) {
        for (const g of e.groups as unknown[]) {
          if (!g || typeof g !== 'object') continue;
          const group = g as { label?: unknown; items?: unknown };
          if (group.label != null) parts.push(String(group.label));
          if (Array.isArray(group.items)) parts.push(...group.items.map((i) => String(i ?? '')));
        }
      }
    }
  };
  for (const raw of sections) {
    if (!raw || typeof raw !== 'object') continue;
    const section = raw as { type?: unknown; title?: unknown; content?: unknown; items?: unknown };
    if (section.title != null) parts.push(String(section.title));
    if (section.type === 'paragraph' || section.type === 'status') {
      parts.push(String(section.content ?? ''));
    } else if (section.type === 'list' && Array.isArray(section.items)) {
      parts.push(...section.items.map((i) => String(i ?? '')));
    } else if (section.type === 'nested_list' && Array.isArray(section.items)) {
      pushNestedItems(section.items as unknown[]);
    }
  }
  return parts.join('\n');
}

// #18 (rule 16: no "Clinical references" numbering with nothing for it to
// resolve to): a pure bracket+digit punctuation check, not a keyword list -
// this is the ONLY signal that decides whether the references panel below
// identifies itself as the numbered "Clinical references" or the un-numbered
// "Guidelines consulted" list.
function hasInlineCitationMarkerText(text: string): boolean {
  return /\[\d+\]/.test(text);
}

/** Prefer snake_case (API/chat); fall back to camelCase trace / client payloads */
export function responseMetaPick<T>(
  meta: Record<string, unknown> | null | undefined,
  snakeKey: string,
  camelKey: string,
): T | undefined {
  if (!meta) return undefined;
  const snake = meta[snakeKey];
  if (snake !== undefined && snake !== null) return snake as T;
  const camel = meta[camelKey];
  if (camel !== undefined && camel !== null) return camel as T;
  return undefined;
}

function normalizeReferencePreview(source: RagSourcePayload): string {
  const preview = String(source.content_preview ?? '').replace(/\s+/g, ' ').trim();
  if (preview) return preview;
  const resource = String(source.resource ?? '').replace(/\s+/g, ' ').trim();
  if (resource && resource !== source.source) return resource;
  return 'Referenced clinical source.';
}

function referenceDedupKey(source: RagSourcePayload): string {
  return [
    String(source.document_id ?? '').trim(),
    String(source.chunk_id ?? '').trim(),
    String(source.source ?? '').trim(),
    normalizeReferencePreview(source).slice(0, 160),
  ].join('|');
}

function formatReferenceScore(score?: string): string | null {
  if (score == null) return null;
  const raw = String(score).trim();
  if (!raw) return null;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return raw;
  if (numeric > 0 && numeric <= 1) return `score ${numeric.toFixed(2)}`;
  return `score ${numeric.toFixed(1)}`;
}

function extractReferenceHref(source: RagSourcePayload): string | null {
  const sourceValue = String(source.source ?? '').trim();
  const resourceValue = String(source.resource ?? '').trim();
  if (/^https?:\/\//i.test(sourceValue)) return sourceValue;
  if (/^https?:\/\//i.test(resourceValue)) return resourceValue;
  return null;
}

interface MessageProps {
  message: any;
  density: string;
  showEvidence: boolean;
  onRetryCommand?: (command: string) => void;
  onSendSuggestion?: (message: string) => void;
  clinicalQualityRerunUserText?: string;
  onClinicalQualityRerun?: (userText: string) => void;
  onSavePrompt?: (title: string, prompt: string) => void;
  onRemoveSavedPrompt?: (prompt: string) => void;
  isPromptSaved?: (prompt: string) => boolean;
}

// Format processing time into ms / s / min / h for readability
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)} h`;
}


// #85: a terminal (scope-declined or clarification) response still carries
// whatever execution_flow code the router happened to stamp (e.g. EEF),
// which mislabels it as "Data query" even though no data query ran. The
// response_contract, when present, identifies these terminals reliably and
// takes precedence over the flow-code map.
const _CONTRACT_LABELS: Record<string, string> = {
  unsupported_message: 'Out of scope',
  clarification_question: 'Clarification',
};

// #111 (Ben-Q5): the Knowledge-RAG route (clinical_evidence_query) is stamped
// with execution_flow EEF, the same code a raw chart data query carries, so the
// flow-code map alone mislabels it "Data query". intent_type identifies the
// route reliably and takes precedence over the flow-code map (mirrors the
// response_contract override above). Only this one intent is remapped; every
// other route falls through to its execution_flow label unchanged.
const _INTENT_LABELS: Record<string, string> = {
  clinical_evidence_query: 'Clinical evidence',
};

export function flowLabel(flow: string, responseContract?: string, intentType?: string): string {
  if (responseContract && _CONTRACT_LABELS[responseContract]) {
    return _CONTRACT_LABELS[responseContract];
  }
  if (intentType && _INTENT_LABELS[intentType]) {
    return _INTENT_LABELS[intentType];
  }
  const labels: Record<string, string> = {
    EEF: 'Data query',
    CARF: 'Clinical analysis',
    DCRF: 'Clinical reasoning',
  };
  return labels[flow] || flow;
}

function RagSpanHighlight({ text, resource }: { text: string; resource: string }) {
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span
          className="cursor-help border-b-2 border-dotted border-brand/60 bg-brand/11 rounded-sm px-0.5 not-prose align-baseline text-[14px] text-foreground/90"
          data-rag-cite="1"
        >
          {text}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-md max-h-72 overflow-y-auto text-left border border-border shadow-lg">
        <p className="text-[10px] uppercase tracking-wide text-primary-foreground/70 mb-1.5">Reference</p>
        <p className="text-xs text-primary-foreground whitespace-pre-wrap leading-relaxed">{resource}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function RagSourcesPanel({
  sources,
  hasInlineCitations,
}: {
  sources: RagSourcePayload[];
  /** #18: when the response text carries no inline "[N]" markers, the panel
   * must identify itself as an un-numbered "Guidelines consulted" list
   * rather than the numbered "Clinical references" - an orphaned numbered
   * panel with nothing in the text pointing at it is a rule-16 violation. */
  hasInlineCitations: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!sources?.length) return null;
  const title = hasInlineCitations ? 'Clinical references' : 'Guidelines consulted';

  return (
    <div className="rounded border border-brand-border bg-linear-to-br from-brand-subtle via-surface-base to-brand-subtle/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-brand-subtle/50 transition-colors"
      >
        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-brand-subtle ring-1 ring-inset ring-brand-border">
          <BookOpen className="w-3.5 h-3.5 text-brand-text" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-foreground">{title}</span>
          <span className="ml-2 text-xs text-text-tertiary">{sources.length} reference{sources.length !== 1 ? 's' : ''}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-3.5 space-y-2 border-t border-brand-border/60">
          {sources.map((s, i) => (
            <div key={`${s.number ?? i + 1}-${s.source ?? i}`} className="pt-2.5 first:pt-2.5 flex items-start gap-2.5">
              {hasInlineCitations && (
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-brand-subtle ring-1 ring-inset ring-brand-border text-[10px] font-medium text-brand-text mt-0.5 shrink-0">
                  {s.number ?? i + 1}
                </div>
              )}
              <div className="min-w-0 flex-1 rounded-lg border border-brand-border/70 bg-surface-raised/70 px-3 py-2.5">
                <div className="text-[13px] font-medium text-foreground/90">
                  {s.source}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? '').trim()).filter(Boolean);
}

// #109: values must match specialty_taxonomy.py's SPECIALTY_GROUPS group_title
// strings exactly (single source of truth, rule 15) - several previously
// drifted (e.g. "Renal and Metabolic Medicine" here vs the canonical
// "Endocrine, Metabolic & Renal"), so the same specialty could read
// differently between this label and the backend-titled CMA report in the
// same response. Keys are unchanged (a separate legacy identifier
// convention from specialty_taxonomy.py's kebab-case technical_id).
function specialtyLabel(raw: string | undefined): string {
  const value = String(raw ?? '').trim();
  if (!value) return 'Specialty';
  const labels: Record<string, string> = {
    PRIMARY_CARE: 'Primary Care',
    EMERGENCY_TRAUMA: 'Emergency & Trauma',
    CARDIOVASCULAR: 'Cardiovascular',
    PULM_CC_SLEEP: 'Pulmonary, Critical Care & Sleep',
    INFECTIOUS_DISEASE: 'Infectious Disease',
    DIGESTIVE_LIVER_NUTRITION: 'Digestive, Liver & Nutrition',
    ENDO_METABOLIC_RENAL: 'Endocrine, Metabolic & Renal',
    UROLOGY_MALE_PELVIC: 'Urology & Male Pelvic Health',
    WOMENS_HEALTH_PREGNANCY: "Women’s Health & Pregnancy",
    PEDS_NEONATAL_GENETICS: 'Pediatrics, Neonatal & Genetics',
    BRAIN_BEHAVIORAL: 'Brain & Behavioral Health',
    EYE_ENT_DERM: 'Eye, Ear, Skin & Head-Neck',
    MSK_RHEUM_REHAB: 'Musculoskeletal, Rheumatology & Rehab',
    BLOOD_CANCER_IMMUNOLOGY: 'Blood, Cancer & Immunology',
    SURGERY_PERIOP: 'Surgery & Perioperative Care',
    DIAGNOSTICS_IMAGING: 'Imaging & Diagnostic Medicine',
    PHARMACOLOGY: 'Pharmacology',
  };
  return labels[value] || value.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export function cleanClinicalText(text: string): string {
  // #107: the prior list also stripped bare "Router" and "Agent" - both are
  // ordinary English/clinical words (e.g. "chemotherapeutic agent",
  // "antimicrobial agent") with no structural way to distinguish a clinical
  // usage from an internal system-name leak, so removing them corrupted
  // legitimate clinical text. Scoped down to the specific multi-word /
  // technical-only internal artifact names, which carry effectively no
  // collision risk with clinical vocabulary.
  return String(text ?? '')
    .replace(/#{1,6}\s*/g, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/\[(?:RAG(?:\s+Direct)?\s+Answer(?:\s+—\s*[^\]]+)?)\]/gi, '')
    .replace(/\[(?:RAG\s+—\s*[^\]]+)\]/gi, '')
    .replace(/\b(?:SpecialtyRouter|Multi-Agent|RAG|execution[_\s-]?metadata|routing information|model name)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function shortClinicalSnippet(text: string, maxLength = 220): string {
  const cleaned = cleanClinicalText(text);
  if (!cleaned) return '';
  const sentenceCut = cleaned.search(/[.!?](\s|$)/);
  const snippet = sentenceCut > 60 ? cleaned.slice(0, sentenceCut + 1) : cleaned;
  if (snippet.length <= maxLength) return snippet;
  return `${snippet.slice(0, maxLength - 1).trimEnd()}...`;
}

function buildSupportingEvidenceSummary(
  output: SpecialtyResultPayload,
  references: RagSourcePayload[],
): string[] {
  const claims = toStringArray(output.grounded_claims).map((item) => cleanClinicalText(item)).filter(Boolean);
  if (claims.length > 0) return claims;
  return references
    .map((reference) => cleanClinicalText(normalizeReferencePreview(reference)))
    .filter(Boolean)
    .slice(0, 3);
}

function buildRecommendedActions(output: SpecialtyResultPayload): string[] {
  return [
    ...toStringArray(output.recommended_additional_data),
    ...toStringArray(output.recommended_followup_questions),
  ].map((item) => cleanClinicalText(item)).filter(Boolean);
}

/** The published demo is a recorded walkthrough, so its follow-ups are shown
 *  as what they are: the questions that were asked, not offers to ask them. */
const DEMO_READONLY = String(import.meta.env.VITE_DEMO_READONLY ?? '').trim().toLowerCase() === 'true';

function SuggestedQuestionsPanel({
  suggestions,
  onSelect,
  readOnly,
}: {
  suggestions: string[];
  onSelect?: (message: string) => void;
  /** Show follow-ups as static pills (e.g. admin trace viewer with no composer). */
  readOnly?: boolean;
}) {
  if (!suggestions.length) return null;
  if (readOnly || DEMO_READONLY) {
    return (
      <div data-demo-followup className="mt-4 rounded-2xl border border-border/70 bg-surface-sunken/18 px-3.5 py-3 sm:px-4">
        <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-text-tertiary sm:text-[11px]">
          Suggested follow-up
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {suggestions.map((item) => (
            <span
              key={item}
              className="rounded-full border border-border/80 bg-background/80 px-3 py-1.5 text-[12px] font-medium text-foreground/90 sm:text-[13px]"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  }
  if (!onSelect) return null;
  return (
    <div className="mt-4 rounded-2xl border border-border/70 bg-surface-sunken/18 px-3.5 py-3 sm:px-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-text-tertiary sm:text-[11px]">
        Suggested Follow-Up
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {suggestions.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onSelect(item)}
            className="rounded-full border border-border bg-background px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-surface-hover sm:text-[13px]"
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}

function buildClinicalOverview(
  overview: string,
  specialtyOutputs: SpecialtyResultPayload[],
  specialties: string[],
): string {
  const cleanedOverview = cleanClinicalText(overview);
  const technicalOverview =
    /output_mode|intent=|routing_inputs|execution_shape|specialties=\[|specialtyrouter|parallel/i.test(overview);
  if (cleanedOverview && !technicalOverview) {
    return shortClinicalSnippet(cleanedOverview, 800);
  }

  const specialtySnippets = specialtyOutputs
    .map((output) => shortClinicalSnippet(String(output.summary ?? output.opinion ?? ''), 220))
    .filter(Boolean)
    .slice(0, 3);
  if (specialtySnippets.length > 0) {
    return shortClinicalSnippet(`Integrated findings: ${specialtySnippets.join(' ')}`, 800);
  }

  const readableSpecialties = specialties.map((item) => specialtyLabel(item)).filter(Boolean);
  if (readableSpecialties.length > 0) {
    return shortClinicalSnippet(
      `Integrated findings across ${readableSpecialties.join(', ')} support a focused review of the detailed clinical findings below.`,
      800,
    );
  }

  return 'Clinical overview unavailable.';
}

function fallbackSpecialtyOutputsFromEvents(events: NormalizedThinkingEvent[]): SpecialtyResultPayload[] {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const raw = events[i]?.raw;
    const outputs = raw?.outputs;
    if (Array.isArray(outputs)) {
      const normalized = outputs.filter((item): item is SpecialtyResultPayload => !!item && typeof item === 'object');
      if (normalized.length > 0) return normalized;
    }
  }

  const collected: SpecialtyResultPayload[] = [];
  const seen = new Set<string>();
  for (const event of events) {
    const raw = event.raw;
    const output = raw?.output;
    if (!output || typeof output !== 'object') continue;
    const specialtyKey = String((output as SpecialtyResultPayload).specialty ?? raw?.specialty ?? '').trim();
    if (!specialtyKey || seen.has(specialtyKey)) continue;
    seen.add(specialtyKey);
    collected.push(output as SpecialtyResultPayload);
  }
  return collected;
}

function fallbackEngagedSpecialtiesFromEvents(events: NormalizedThinkingEvent[]): string[] {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const raw = events[i]?.raw;
    const completed = raw?.specialties_completed;
    if (Array.isArray(completed) && completed.length > 0) {
      return completed.map((item) => String(item ?? '').trim()).filter(Boolean);
    }
    const specialties = raw?.specialties;
    if (Array.isArray(specialties) && specialties.length > 0) {
      return specialties.map((item) => String(item ?? '').trim()).filter(Boolean);
    }
  }
  return [];
}

function fallbackRouterOverviewFromEvents(
  events: NormalizedThinkingEvent[],
  specialtyOutputs: SpecialtyResultPayload[],
): string {
  if (specialtyOutputs.length > 0) {
    return 'Integrated findings are available from the engaged clinical specialties. Review the detailed clinical findings below.';
  }
  return '';
}

function RouterOverviewPanel({
  overview,
  specialties,
}: {
  overview: string;
  specialties: string[];
}) {
  const resolvedOverview = overview.trim() || 'Overview unavailable.';
  return (
    <div className="mt-4 rounded border border-(--chart-categorical-6)/30 bg-linear-to-br from-(--chart-categorical-6)/10 via-surface-base to-(--chart-categorical-6)/5 p-3.5 shadow-sm sm:p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-(--chart-categorical-6)/10 ring-1 ring-inset ring-(--chart-categorical-6)/30">
          <BarChart3 className="h-4 w-4 text-(--chart-categorical-6)" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-medium text-foreground sm:text-sm">Clinical Overview</h3>
            {specialties.length > 0 ? (
              <span className="inline-flex rounded-full border border-(--chart-categorical-6)/30 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-(--chart-categorical-6)">
                {specialties.length} specialty{specialties.length === 1 ? '' : 'ies'}
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-[14px] font-medium leading-6 text-foreground/90 sm:text-sm">{resolvedOverview}</p>
        </div>
      </div>
    </div>
  );
}

function DetailList({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">{title}</div>
      <ul className="space-y-1 text-[14px] leading-6 text-foreground/85 sm:text-[13px] sm:leading-5">
        {items.map((item, idx) => (
          <li key={`${title}-${idx}`} className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/55" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SpecialtyReferenceList({ references }: { references: RagSourcePayload[] }) {
  if (!references.length) {
    return <div className="text-[13px] leading-6 text-text-tertiary">No references available.</div>;
  }

  return (
    <ul className="space-y-2 text-[13px] leading-5 text-foreground/85">
      {references.map((reference, idx) => {
        const href = extractReferenceHref(reference);
        const title = String(reference.source ?? '').trim() || 'Clinical reference';
        const subtitle = String(reference.resource ?? '').trim() || normalizeReferencePreview(reference);
        const score = formatReferenceScore(reference.score);
        return (
          <li key={`${referenceDedupKey(reference)}-${idx}`} className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/55" />
            <div className="min-w-0">
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  // `noreferrer` already implies `noopener` in current browsers; both
                  // are named so the guarantee does not depend on that implication.
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline underline-offset-2"
                >
                  {title}
                </a>
              ) : (
                <div className="font-medium text-foreground/90">{title}</div>
              )}
              <div className="text-text-tertiary wrap-break-word">{subtitle}</div>
              {score ? (
                <div className="mt-0.5 text-[11px] uppercase tracking-wide text-text-tertiary">{score}</div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Option A CMA per-branch RAG — citation-free reference list + hover tooltip +
// per-category answer area (renders from responseMetadata.guideline_evidence).
// ---------------------------------------------------------------------------

// Display labels for the closed CMA category enum (display-only, not routing).
const CMA_CATEGORY_LABELS: Record<string, string> = {
  differential: 'Differential Diagnosis',
  treatment: 'Treatment Planning',
  care_gaps: 'Care Gaps & Prevention',
  safety: 'Medication & Procedure Safety',
};

function cmaCategoryLabel(cat?: string | null): string {
  const v = String(cat ?? '').trim();
  if (!v) return 'Evidence';
  return (
    CMA_CATEGORY_LABELS[v] ??
    v.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Title-case the backend document_type enum into a "Guideline"/"Textbook"
// badge (generic transform, not a hardcoded word list). #81 (Eleanor round,
// user order): no [] brackets around the badge text.
function formatDocumentTypeBadge(dt?: string | null): string | null {
  const v = String(dt ?? '').trim();
  if (!v) return null;
  const titled = v
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return titled || null;
}

// #81 (user order): Textbook vs Guideline badges in two distinct theme-aware
// colors. Display-only style map over the closed backend enum (same pattern as
// CMA_CATEGORY_LABELS) — never used for routing/logic.
const DOCUMENT_TYPE_BADGE_CLASSES: Record<string, string> = {
  guideline:
    'bg-emerald-500/15 text-emerald-700 ring-emerald-500/40 dark:text-emerald-300 dark:ring-emerald-400/40',
  textbook:
    'bg-sky-500/15 text-sky-700 ring-sky-500/40 dark:text-sky-300 dark:ring-sky-400/40',
};

function documentTypeBadgeClass(dt?: string | null): string {
  const v = String(dt ?? '').trim().toLowerCase();
  return (
    DOCUMENT_TYPE_BADGE_CLASSES[v] ?? 'bg-brand-subtle text-brand-text ring-brand-border'
  );
}

// USER ORDER 2026-08-03: the raw citation number(s) this reference answers
// for must appear at the start of its line, matching the [n] marker(s) the
// clinician sees inline in the answer text — never a renumbered position.
function formatReferenceNumberPrefix(passages: GuidelineEvidencePassage[]): string | null {
  const numbers = Array.from(
    new Set(
      passages
        .map((p) => (p.number != null ? String(p.number).trim() : ''))
        .filter((n) => n.length > 0),
    ),
  ).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
  });
  return numbers.length ? `[${numbers.join(', ')}]` : null;
}

function formatPassagePages(p: GuidelineEvidencePassage): string | null {
  const start =
    p.page_start != null && String(p.page_start).trim() !== '' ? String(p.page_start).trim() : null;
  const end =
    p.page_end != null && String(p.page_end).trim() !== '' ? String(p.page_end).trim() : null;
  if (start && end && start !== end) return `pp. ${start}–${end}`;
  if (start) return `p. ${start}`;
  if (end) return `p. ${end}`;
  return null;
}

function GuidelineReferenceItem({ reference }: { reference: GuidelineEvidenceReference }) {
  // #72 (Kira round, user order): the hover tooltip is replaced by a
  // click-opened dialog — a hover surface dismisses the moment the pointer
  // leaves, so its text can never be scrolled or copied. The WHOLE row
  // (doc-type badge included) is the trigger; the dialog is modal, scrollable,
  // text-selectable, and closes via its X, the overlay, or Escape.
  const passages = reference.passages ?? [];
  const docType =
    reference.document_type ?? passages.find((p) => p.document_type)?.document_type ?? null;
  const badge = formatDocumentTypeBadge(docType);
  const title = String(reference.doc_title ?? '').trim() || 'Clinical reference';
  const numberPrefix = formatReferenceNumberPrefix(passages);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`View ${passages.length} retrieved passage${passages.length === 1 ? '' : 's'} from ${title}`}
          className="flex w-full items-start gap-2 rounded-md px-1.5 py-1 text-left text-[13px] leading-5 text-foreground/90 transition-colors hover:bg-brand-subtle/60 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-brand-text"
        >
          <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-text" />
          <span className="min-w-0 flex-1">
            {badge ? (
              <span
                className={`mr-1.5 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${documentTypeBadgeClass(docType)}`}
              >
                {badge}
              </span>
            ) : null}
            {numberPrefix ? (
              <span className="mr-1 font-mono text-[11px] font-medium text-brand-text">
                {numberPrefix}
              </span>
            ) : null}
            <span className="font-medium">{title}</span>
          </span>
          <span className="mt-px inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-brand-text ring-1 ring-inset ring-brand-border">
            {passages.length} passage{passages.length === 1 ? '' : 's'}
          </span>
        </button>
      </DialogTrigger>
      {/* #92 (user design): solid dialog width with a hard viewport cap at
          every breakpoint — the box never sizes to its content, so a long
          source title can no longer push it out of the window. */}
      <DialogContent className="w-[640px] max-w-[calc(100vw-2rem)] p-0 sm:max-w-[calc(100vw-2rem)]">
        <div className="border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2 pr-8">
            {badge ? (
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${documentTypeBadgeClass(docType)}`}
              >
                {badge}
              </span>
            ) : null}
            {/* #92 (user design): long titles wrap onto multiple lines inside
                the fixed-width dialog instead of truncating or stretching it. */}
            <DialogTitle className="min-w-0 whitespace-normal wrap-break-word text-[14px] font-semibold leading-5 text-foreground">
              {numberPrefix ? <span className="mr-1 font-mono font-medium">{numberPrefix}</span> : null}
              {title}
            </DialogTitle>
          </div>
          <div className="mt-1 text-[11px] text-text-tertiary">
            {passages.length} passage{passages.length === 1 ? '' : 's'} retrieved
          </div>
        </div>
        <ScrollArea className="max-h-[65vh]">
          <div className="space-y-2 p-3">
            {passages.length === 0 ? (
              <div className="px-1 text-[12px] text-text-tertiary">No passage text captured.</div>
            ) : (
              passages.map((p, i) => {
                const pages = formatPassagePages(p);
                const text = String(p.passage_text ?? p.snippet ?? '').trim();
                // #79 (user-diagnosed): the long V3 section breadcrumb repeated
                // the full document title in every passage header and pushed the
                // card wider than the dialog. The doc title lives ONCE in the
                // DialogTitle; each passage header carries the page only.
                return (
                  <div
                    key={i}
                    className="min-w-0 overflow-hidden rounded-lg border border-border/60 bg-surface-sunken/30"
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border/50 px-2.5 py-1.5">
                      <span className="text-[11px] font-medium text-foreground/85">
                        {pages ?? `Passage ${i + 1}`}
                      </span>
                      {p.category ? (
                        <span className="ml-auto text-[10px] uppercase tracking-wide text-brand-text/80">
                          {cmaCategoryLabel(p.category)}
                        </span>
                      ) : null}
                    </div>
                    {/* #80 (user order): pitch-black background, white serif
                        text, fixed 4-line box (leading-5 x4 + py-2 = h-24) with
                        internal scroll, and "…" at both ends marking the
                        mid-document excerpt. */}
                    <div className="h-24 overflow-y-auto bg-black px-2.5 py-2">
                      <p className="select-text whitespace-pre-wrap font-serif text-[12px] leading-5 text-white">
                        {text ? `… ${text} …` : 'No passage text captured.'}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function GuidelineEvidencePanel({ evidence }: { evidence: GuidelineEvidencePayload }) {
  // #71 (Kira round, user order): the "Evidence by section" digest block is
  // gone — the panel is sources-only. The digests re-summarized retrieval
  // output the review body already reflects, and the user asked for them out.
  const [expanded, setExpanded] = useState(false);
  const references = (evidence.references ?? []).filter(
    (r): r is GuidelineEvidenceReference =>
      !!r && typeof r === 'object' && String(r.doc_title ?? '').trim().length > 0,
  );
  if (!references.length) return null;

  return (
    <div className="rounded border border-brand-border bg-linear-to-br from-brand-subtle via-surface-base to-brand-subtle/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-brand-subtle/50 transition-colors"
      >
        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-brand-subtle ring-1 ring-inset ring-brand-border">
          <BookOpen className="w-3.5 h-3.5 text-brand-text" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-foreground">Clinical references</span>
          <span className="ml-2 text-xs text-text-tertiary">
            {references.length} reference{references.length === 1 ? '' : 's'}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-brand-border/60">
          {evidence.review_incomplete ? (
            <p className="pt-2 text-[12px] leading-5 text-text-tertiary">
              Evidence was retrieved, but the specialty review did not complete — nothing in this
              response cites these sources.
            </p>
          ) : null}
          <div className="space-y-1 pt-1">
            <div className="text-[11px] font-medium uppercase tracking-wide text-brand-text">Sources</div>
            <div className="space-y-0.5">
              {references.map((r, i) => (
                <GuidelineReferenceItem key={i} reference={r} />
              ))}
            </div>
            <p className="pt-1 text-[11px] text-text-tertiary">
              Click a source to view the retrieved passages.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecialtyDetailedResultsPanel({
  outputs,
  skippedSpecialties,
}: {
  outputs: SpecialtyResultPayload[];
  skippedSpecialties: string[];
}) {
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [expandedSpecialty, setExpandedSpecialty] = useState<string | null>(outputs[0]?.specialty ?? null);
  const specialtyCount = outputs.length;

  return (
    <div className="mt-4 rounded-2xl border border-border/70 bg-surface-raised/80 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpandedPanel((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-sunken/35"
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-inset ring-primary/15">
          <BookOpen className="h-3.5 w-3.5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground">Detailed Clinical Findings</div>
          <div className="text-xs text-text-tertiary">
            {specialtyCount > 0
              ? 'Expand to review specialty-specific assessments, clinical reasoning, evidence summaries, and references.'
              : 'No specialty results available.'}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-text-tertiary transition-transform duration-200 ${expandedPanel ? 'rotate-180' : ''}`} />
      </button>

      {expandedPanel ? (
        <div className="space-y-3 border-t border-border/50 bg-surface-sunken/10 p-3 animate-in fade-in slide-in-from-top-1 duration-200">
          {specialtyCount === 0 ? (
            <div className="rounded-lg border border-border/60 bg-background/90 px-3 py-3 text-sm text-text-tertiary">
              No specialty results available.
            </div>
          ) : null}
          {skippedSpecialties.length > 0 ? (
            <div className="rounded-lg border border-(--status-attention)/30 bg-status-attention-bg px-3 py-2 text-xs text-status-attention-text">
              Some specialty findings could not be completed because supporting evidence was not available: {skippedSpecialties.map((item) => specialtyLabel(item)).join(', ')}.
            </div>
          ) : null}

          {specialtyCount > 0 ? outputs.map((output, idx) => {
            const specialty = specialtyLabel(output.specialty);
            const specialtyKey = String(output.specialty ?? idx);
            const assessment = cleanClinicalText(String(output.summary ?? output.opinion ?? '').trim());
            const reasoning = cleanClinicalText(String(output.supporting_rationale ?? output.opinion ?? output.summary ?? '').trim());
            const missingEvidenceAndLimits = [...toStringArray(output.missing_evidence), ...toStringArray(output.limitations)];
            const references = filterClinicalReferenceSources(
              (
                Array.isArray(output.metadata?.rag_sources)
                  ? output.metadata?.rag_sources
                  : Array.isArray(output.references)
                    ? output.references
                    : []
              ) as RagSourcePayload[],
            );
            const supportingEvidenceSummary = buildSupportingEvidenceSummary(output, references);
            const recommendedActions = buildRecommendedActions(output);
            const collapsedSummary =
              shortClinicalSnippet(assessment || reasoning, 240) ||
              'Expand to review this specialty assessment.';
            const isOpen = expandedSpecialty === specialtyKey;

            return (
              <div key={specialtyKey} className="overflow-hidden rounded-2xl border border-border/60 bg-background/90">
                <button
                  type="button"
                  onClick={() => {
                    setExpandedSpecialty((current) => (current === specialtyKey ? null : specialtyKey));
                  }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-sunken/30"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">{specialty}</div>
                    <div className="mt-1 text-xs leading-5 text-text-tertiary line-clamp-2">
                      {collapsedSummary}
                    </div>
                    <div className="mt-1 text-[11px] text-text-tertiary">
                      {references.length} reference{references.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-text-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen ? (
                  <div className="space-y-4 border-t border-border/40 px-4 py-4 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="space-y-1.5">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Clinical Assessment</div>
                      <div className="text-[13px] leading-6 text-foreground/90 whitespace-pre-wrap">
                        {assessment || 'No specialty assessment available.'}
                      </div>
                    </div>

                    {reasoning ? (
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Clinical Reasoning</div>
                        <div className="text-[13px] leading-6 text-foreground/85 whitespace-pre-wrap">{reasoning}</div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Clinical Reasoning</div>
                        <div className="text-[13px] leading-6 text-text-tertiary">No detailed clinical reasoning available.</div>
                      </div>
                    )}

                    {recommendedActions.length > 0 ? (
                      <DetailList title="Recommended Clinical Actions" items={recommendedActions} />
                    ) : (
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Recommended Clinical Actions</div>
                        <div className="text-[13px] leading-6 text-text-tertiary">No specific clinical actions were provided.</div>
                      </div>
                    )}

                    {supportingEvidenceSummary.length > 0 ? (
                      <DetailList title="Supporting Evidence Summary" items={supportingEvidenceSummary} />
                    ) : (
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Supporting Evidence Summary</div>
                        <div className="text-[13px] leading-6 text-text-tertiary">No supporting evidence summary available.</div>
                      </div>
                    )}

                    {missingEvidenceAndLimits.length > 0 ? (
                      <DetailList title="Missing Information or Limitations" items={missingEvidenceAndLimits.map((item) => cleanClinicalText(item)).filter(Boolean)} />
                    ) : (
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Missing Information or Limitations</div>
                        <div className="text-[13px] leading-6 text-text-tertiary">No significant limitations identified.</div>
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">References</div>
                      <SpecialtyReferenceList references={references} />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          }) : null}
        </div>
      ) : null}
    </div>
  );
}


// Helper function to extract text from widget data
function extractTextFromWidget(widget: any): string {
  if (!widget || !widget.data) return '';
  
  const data = widget.data;
  let text = '';
  
  switch (widget.type) {
    case 'W_SUMMARY_CLINICAL':
      text = data.summary || '';
      if (data.keyPoints) {
        text += '\n\nKey Points:\n' + data.keyPoints.map((p: string) => `• ${p}`).join('\n');
      }
      break;
    case 'W_RISK_PROFILE':
      text = `Risk Level: ${data.level}\nScore: ${data.score}\n`;
      if (data.factors) {
        text += '\nFactors:\n' + data.factors.map((f: any) => `• ${f.name}: ${f.value}`).join('\n');
      }
      break;
    case 'W_NOTE_DRAFT':
      if (data.sections) {
        text = data.sections.map((s: any) => `${s.heading}\n${s.content}`).join('\n\n');
      }
      break;
    case 'W_PLAN_DRAFT':
      if (data.phases) {
        text = data.phases.map((p: any) => `${p.name}\n${p.description || ''}`).join('\n\n');
      }
      break;
    case 'W_TWIN_SNAPSHOT':
      text = `Status: ${data.status}\n`;
      if (data.metrics) {
        text += '\nMetrics:\n' + Object.entries(data.metrics).map(([k, v]) => `${k}: ${v}`).join('\n');
      }
      break;
    case 'W_TWIN_SIMULATION':
      text = `Baseline Risk: ${data.baselineRisk}%\nScenario Risk: ${data.scenarioRisk}%\nDelta: ${data.delta}%\n`;
      break;
    case 'W_ALERT_DETAIL':
      text = `${data.title || ''}\n${data.description || ''}\n`;
      if (data.recommendations) {
        text += '\nRecommendations:\n' + data.recommendations.map((r: string) => `• ${r}`).join('\n');
      }
      break;
    case 'W_ASSESS_OVERVIEW':
    case 'W_ASSESS_DETAIL':
      if (data.summary) text = data.summary;
      if (data.findings) {
        text += '\n\nFindings:\n' + data.findings.map((f: any) => `• ${f.label}: ${f.value}`).join('\n');
      }
      break;
    case 'W_ASSESS_REDFLAGS':
      if (data.flags) {
        text = data.flags.map((f: any) => `• ${f.title || f.label}: ${f.description || f.value || ''}`).join('\n');
      }
      break;
    case 'W_RISK_DRIVERS':
      if (data.drivers) {
        text = data.drivers.map((d: any) => `• ${d.name || d.label}: ${d.value || d.impact || ''}`).join('\n');
      }
      break;
    case 'W_ALERT_INBOX':
      if (data.alerts) {
        text = data.alerts.map((a: any) => `• ${a.title || a.id}: ${a.description || a.message || ''}`).join('\n');
      }
      break;
    case 'W_PLAN_DIFF':
      if (data.changes) {
        text = data.changes.map((c: any) => `• ${c.type || 'Change'}: ${c.description || c.detail || ''}`).join('\n');
      }
      break;
    case 'W_SIMULATION_HISTORY':
      if (data.simulations) {
        text = data.simulations.map((s: any) => `• ${s.id || s.date}: ${s.description || s.result || ''}`).join('\n');
      }
      break;
    case 'W_AUDIT':
      if (data.entries) {
        text = data.entries.map((e: any) => `• ${e.action || e.type}: ${e.description || e.detail || ''}`).join('\n');
      }
      break;
    case 'W_FILE_PDF':
      text = `File: ${data.filename || data.name || 'PDF Document'}\n`;
      if (data.description) text += data.description;
      break;
    case 'W_EXPORT_LIST':
      if (data.exports && Array.isArray(data.exports)) {
        text = data.exports.map((e: any) => `• ${e.filename ?? e.exportId ?? 'export'} (${e.type ?? ''}) ${e.timestamp ?? ''}`).join('\n');
      }
      break;
    case 'W_MEDICATIONS':
    case 'W_ENCOUNTERS':
    case 'W_CONDITIONS':
    case 'W_ALLERGIES':
    case 'W_OBSERVATIONS':
    case 'W_PROCEDURES':
    case 'W_IMMUNIZATIONS':
    case 'W_REPORTS':
    case 'W_CAREPLANS':
      if (data.items && Array.isArray(data.items)) {
        text = data.items.map((it: any) => {
          const d = it._display ?? it.name ?? it.vaccine_name ?? it.display_name ?? it.medication ?? it.type ?? it.substance;
          const extra = it.dose ?? it.date ?? it.occurrence_date ?? it.status ?? it.reaction ?? it.performed_date ?? it.effective_date;
          return extra ? `• ${d} — ${extra}` : `• ${d}`;
        }).join('\n');
      }
      break;
    case 'W_CHART_LINE':
      text = `Chart: ${data.title || 'Data Chart'}\n`;
      if (data.description) text += data.description + '\n';
      if (data.points && Array.isArray(data.points)) {
        text += `Data Points: ${data.points.length}\n`;
        if (data.points.length > 0) {
          const first = data.points[0];
          const last = data.points[data.points.length - 1];
          text += `Range: ${first.date || first.x} to ${last.date || last.x}\n`;
        }
      }
      break;
    case 'W_FHIR_PATIENT':
      if (data.configured && data.found) {
        text = `Patient record: ${data.name ?? data.fhirId ?? data.patientId}\n`;
        if (data.birthDate) text += `Birth date: ${data.birthDate}\n`;
        if (data.gender) text += `Gender: ${data.gender}\n`;
      } else {
        text = data.message ?? 'Patient record is not available.';
      }
      break;
    default:
      // Fallback: try to extract any text-like properties
      if (typeof data === 'object') {
        const textProps = Object.entries(data)
          .filter(([_, v]) => typeof v === 'string' && v.length > 0)
          .map(([k, v]) => `${k}: ${v}`)
          .join('\n');
        text = textProps;
      }
  }
  
  return text;
}

// Helper function to check if message has copyable content
function hasCopyableContent(message: any): boolean {
  const intentType =
    typeof message.responseMetadata?.intent_type === 'string'
      ? message.responseMetadata.intent_type
      : undefined;
  const narrative = message.answer ?? message.content;
  if (typeof narrative === 'string' && narrative.trim()) return true;
  const structured = structuredPayloadForChartBriefingView(
    intentType,
    message.responseMetadata?.structured as StructuredResponse | undefined,
  );
  if (structured) return true;
  const widgets = widgetsForChartBriefingView(
    intentType,
    message.widgets as { type: string; data: Record<string, unknown> }[] | undefined,
    message.widget as { type: string; data: Record<string, unknown> } | undefined,
  );
  for (const w of widgets) {
    if (extractTextFromWidget(w).trim()) return true;
  }
  return false;
}

function looksLikeMarkdown(text: string): boolean {
  const t = (text || '').trim();
  if (!t) return false;
  return /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|>\s)|\*\*[^*]+\*\*|`[^`]+`|\|.+\|/.test(t);
}

export function normalizeClinicalMarkdown(text: string): string {
  let t = (text || '').replace(/\r\n/g, '\n').trim();
  if (!t) return t;

  t = t
    .replace(/^\s*\*\*(?=[A-Za-z0-9])/gm, '')
    .replace(/\*\*\s*$/gm, '')
    .replace(/[ \t]+\n/g, '\n');
  t = t.replace(/(?<!^)(?<!\n)\s+(?=[A-Z][A-Z0-9/&()'\-]+(?: [A-Z][A-Z0-9/&()'\-]+)+:\s)/g, '\n\n');
  t = t.replace(/(?<=[.!?])\s+(?=(?:Assessment|Plan|Summary|Clinical Reasoning|Recommendation|Recommendations|Overview|Findings|Next Steps|Warnings?|Action Items|Details):)/gi, '\n\n');

  const rawLines = t.split('\n').map((line) => line.trimEnd());
  const tableSepRe = /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/;
  const normalizePipeRow = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim()).filter(Boolean);
  const lines: string[] = [];
  for (let i = 0; i < rawLines.length; i += 1) {
    const line = rawLines[i];
    const trimmed = line.trim();
    const currentCells = trimmed.includes('|') ? normalizePipeRow(trimmed) : [];
    const next = rawLines[i + 1]?.trim() ?? '';
    const nextCells = next.includes('|') ? normalizePipeRow(next) : [];
    if (currentCells.length >= 2) {
      if (nextCells.length >= 2 && !tableSepRe.test(next)) {
        lines.push(`| ${currentCells.join(' | ')} |`);
        lines.push(`| ${currentCells.map(() => '---').join(' | ')} |`);
        continue;
      }
      if (nextCells.length === 0 && !tableSepRe.test(trimmed)) {
        lines.push(currentCells.join(' | '));
        continue;
      }
    }
    lines.push(line);
  }
  const headingOnlyRe = /^\s*([A-Z][A-Za-z0-9/&()'., \-]{1,100})\s*:\s*$/;
  const inlineHeadingRe = /^\s*([A-Z][A-Za-z0-9/&()'., \-]{1,60})\s*:\s+(.+?)\s*$/;

  const out: string[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      if (out.length > 0 && out[out.length - 1] !== '') out.push('');
      continue;
    }

    const headingOnly = headingOnlyRe.exec(line);
    if (headingOnly) {
      if (out.length > 0 && out[out.length - 1] !== '') out.push('');
      out.push(`## ${headingOnly[1].trim()}`);
      out.push('');
      continue;
    }

    const inlineHeading = inlineHeadingRe.exec(line);
    if (inlineHeading) {
      const [, label, body] = inlineHeading;
      const labelTrim = label.trim();
      const labelWords = labelTrim.split(/\s+/);
      const isAllCaps = labelTrim === labelTrim.toUpperCase();
      // Guard against promoting a proper-noun / title phrase whose colon is
      // part of the name (e.g. "The Kidney Disease: Improving Global Outcomes"
      // — the KDIGO expansion), which is NOT a section heading. Detected purely
      // by capitalization shape — no word lists: a multi-word Title-Case label
      // whose body ALSO opens in Title Case (a proper-noun run) is a name, so
      // leave it as plain prose and keep the colon (#88). A genuine section
      // heading is ALL-CAPS, or has a body that reads as a sentence.
      const bodyLead = body.trim().split(/\s+/).slice(0, 3);
      const titleCaseBody =
        bodyLead.length >= 2 && bodyLead.filter((w) => /^[A-Z][a-z]/.test(w)).length >= 2;
      const titlePhraseLabel = labelWords.length >= 2 && labelWords.every((w) => /^[A-Z]/.test(w));
      const isProperNounName = !isAllCaps && titlePhraseLabel && titleCaseBody;
      if (
        !isProperNounName &&
        labelWords.length <= 6 &&
        (isAllCaps || body.length >= 48 || /[.!?;]/.test(body))
      ) {
        if (out.length > 0 && out[out.length - 1] !== '') out.push('');
        out.push(`## ${labelTrim}`);
        out.push('');
        out.push(body.trim());
        out.push('');
        continue;
      }
    }

    if (/^(Warning|Warnings?)\s*:\s+/i.test(line)) {
      out.push(`> ${line.split(':', 2)[1].trim()}`);
      out.push('');
      continue;
    }

    out.push(rawLine);
  }

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim() || t;
}

function buildNarrativeWidgetMarkdown(data: Record<string, unknown>): string {
  const orderedKeys = ['summary', 'details', 'assessment', 'impression', 'recommendation', 'plan', 'message', 'content'];
  const chunks: string[] = [];
  for (const key of orderedKeys) {
    const value = data[key];
    if (typeof value !== 'string' || !value.trim()) continue;
    const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
    const body = value.trim();
    chunks.push(looksLikeMarkdown(body) ? body : `**${label}**\n${body}`);
  }
  return chunks.join('\n\n').trim();
}

function MarkdownAnswer({
  text,
  codeReferences,
  scopeId,
  ragSpans,
  className,
}: {
  text: string;
  codeReferences?: CodeRef[];
  scopeId?: string;
  ragSpans?: RagSpanPayload[];
  className?: string;
}) {
  const processedText = React.useMemo(() => {
    let t = text.replace(/<br\s*\/?>/gi, '\n');
    t = normalizeClinicalMarkdown(t);
    if (codeReferences && codeReferences.length > 0 && scopeId) {
      t = injectCodeMarkers(t, codeReferences, scopeId);
    }
    return t;
  }, [text, codeReferences, scopeId]);


  const segmentedParts = React.useMemo(() => {
    if (!ragSpans?.length || !processedText.length) return null;
    const n = processedText.length;
    const sorted = [...ragSpans]
      .filter(
        (s) =>
          typeof s.start === 'number' &&
          typeof s.end === 'number' &&
          typeof s.resource === 'string' &&
          s.start >= 0 &&
          s.end <= n &&
          s.start < s.end
      )
      .sort((a, b) => a.start - b.start);
    if (!sorted.length) return null;
    type Part = { kind: 'md'; t: string } | { kind: 'rag'; t: string; resource: string };
    const out: Part[] = [];
    let cur = 0;
    let lastEnd = -1;
    for (const s of sorted) {
      if (s.start < lastEnd) continue;
      if (s.start > cur) out.push({ kind: 'md', t: processedText.slice(cur, s.start) });
      out.push({ kind: 'rag', t: processedText.slice(s.start, s.end), resource: s.resource });
      cur = s.end;
      lastEnd = s.end;
    }
    if (cur < n) out.push({ kind: 'md', t: processedText.slice(cur) });
    return out;
  }, [processedText, ragSpans]);

  if (segmentedParts && segmentedParts.length > 0) {
    return (
      <div className={className || MARKDOWN_PROSE_CLASS}>
        {segmentedParts.map((part, i) =>
          part.kind === 'rag' ? (
            <RagSpanHighlight key={`r-${i}`} text={part.t} resource={part.resource} />
          ) : part.t ? (
            <ClinicalMarkdownBody key={`m-${i}`} content={part.t} />
          ) : null
        )}
      </div>
    );
  }

  return (
    <ClinicalMarkdownBody content={processedText} className={className || MARKDOWN_PROSE_CLASS} />
  );
}

function looksLikeRawJson(text: string): boolean {
  const t = text.trim();
  return t.startsWith('[') || t.startsWith('{');
}

/**
 * When true, render the streamed assistant markdown (e.g. "## Direct Answer …") instead of the full
 * structured treatment critique card and omit duplicate specialty overview panels below.
 */
export function shouldPreferSpecialtyTranscriptMarkdown(message: any): boolean {
  const structured = message?.responseMetadata?.structured as StructuredResponse | undefined;
  if (!structured) return false;
  let answer = message.answer ? String(message.answer) : '';
  if (answer && looksLikeRawJson(answer)) {
    answer = '';
  }
  if (!answer.trim()) return false;
  const preferNarrative = structured.meta?.prefer_narrative === true;
  const renderHint = String(structured.meta?.render_hint || '').trim();
  const outputMode = String(structured.meta?.output_mode || '').trim().toUpperCase();
  // Render mode comes only from the structured output_mode enum /
  // prefer_narrative flag — never from sniffing the (LLM-authored) title text.
  const treatmentCritique = outputMode === 'TREATMENT_CRITIQUE';
  const isSpecialtyStructured =
    renderHint === 'specialty' || structured.response_type === 'specialty';
  return (preferNarrative || treatmentCritique) && isSpecialtyStructured;
}

// StructuredDataSupplement removed — structured data is now integrated directly into the response

export function StructuredOrMarkdown({
  message,
  answerScopeId,
  preferTranscriptMarkdown = false,
}: {
  message: any;
  /** Same id used for Codes & References anchors and in-text code links */
  answerScopeId: string;
  /**
   * When true and {@link message.answer} is non-empty, render that markdown as the main body
   * (clinician transcript) instead of {@link message.responseMetadata.structured}.
   * Used for Request Topology replay so the admin view matches the streamed user-visible message.
   */
  preferTranscriptMarkdown?: boolean;
}) {
  const intentType =
    typeof message.responseMetadata?.intent_type === 'string'
      ? message.responseMetadata.intent_type
      : undefined;
  const structured = structuredPayloadForChartBriefingView(
    intentType,
    message.responseMetadata?.structured as StructuredResponse | undefined,
  );
  let answer = message.answer ? String(message.answer) : '';
  if (!answer && message.content) {
    answer = String(message.content);
  }
  const useStructured = hasUsefulStructuredContent(structured);
  const widgets = widgetsForChartBriefingView(
    intentType,
    message.widgets as { type: string; data: Record<string, unknown> }[] | undefined,
    message.widget as { type: string; data: Record<string, unknown> } | undefined,
  );
  const preferNarrative = structured?.meta?.prefer_narrative === true;
  const renderHint = String(structured?.meta?.render_hint || '').trim();
  const narrativePreferredOnly = preferNarrative && (renderHint === 'specialty' || structured?.response_type === 'specialty');
  const codeReferences = (message.responseMetadata?.code_references ?? []) as CodeRef[];
  const rawRagSpans = message.responseMetadata?.rag_spans as RagSpanPayload[] | undefined;
  // Offsets are for the final answer; applying while tokens stream misaligns highlights.
  const ragSpans = rawRagSpans?.length && !message.isStreaming ? rawRagSpans : undefined;
  const scopeId = answerScopeId;

  const codeRefSig = React.useMemo(
    () => codeReferences.map((r) => `${r.system}\t${r.code}\t${r.term}`).join('\n'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(codeReferences.map((r) => [r.system, r.code, r.term]))],
  );
  const codeUiCtx = React.useMemo(() => {
    if (message.isStreaming || !codeReferences.length) return null;
    return { refs: codeReferences, scopeId: answerScopeId };
  }, [message.isStreaming, answerScopeId, codeRefSig, codeReferences.length]);

  let body: React.ReactNode = null;

  if (narrativePreferredOnly && answer) {
    const isChartBriefing = intentType === 'chart_briefing';
    body = (
      <div className={isChartBriefing ? 'space-y-4' : 'space-y-5'}>
        <MarkdownAnswer
          text={answer}
          codeReferences={codeReferences}
          scopeId={scopeId}
          ragSpans={ragSpans}
          className={isChartBriefing ? CHART_BRIEFING_PROSE_CLASS : undefined}
        />
      </div>
    );
  } else if (useStructured) {
    body = (
      <div>
        <FinalResponseRenderer
          structured={structured as import('./response/types').StructuredResponse}
          fallbackText={answer}
          codeReferences={codeReferences}
        />
      </div>
    );
  } else if (answer) {
    // Use chart_briefing specific styling for chart_briefing intent type
    const isChartBriefing = intentType === 'chart_briefing';
    body = (
      <div className={isChartBriefing ? 'space-y-4' : 'space-y-5'}>
        <MarkdownAnswer
          text={answer}
          codeReferences={codeReferences}
          scopeId={scopeId}
          ragSpans={ragSpans}
          className={isChartBriefing ? CHART_BRIEFING_PROSE_CLASS : undefined}
        />
      </div>
    );
  } else if (widgets.length > 0 && !message.isStreaming) {
    // #178: widgets can arrive mid-run as a fallback synthesized straight from an
    // in-flight tool result (see commandApi.ts synthesizeWidgetFromSse -
    // get_medications/get_allergies/get_observations/etc all synthesize a
    // W_MEDICATIONS/W_ALLERGIES/W_OBSERVATIONS widget the moment that single tool
    // call succeeds, well before the run's final answer/metadata event). Rendering
    // this branch unconditionally showed that interim chart data as its own card
    // while the query was still in flight. Gating on `!message.isStreaming` defers
    // rendering until the run completes; the same branch (and its final-response
    // shape) is unchanged for widget-only completed responses.
    body = (
      <div className="space-y-4">
        {widgets.map((w: { type: string; data: Record<string, unknown> }, i: number) => {
          const title = (w.data as Record<string, unknown>)?.title as string | undefined;
          return (
            <div key={i} className={widgets.length > 1 ? 'rounded-lg border border-border p-3' : ''}>
              {title && <h4 className="text-sm font-medium text-foreground mb-2">{title}</h4>}
              {formatStructuredData(w)}
            </div>
          );
        })}
      </div>
    );
  }

  // #75 (Gutmann round): a turn rebuilt from the server's text-only archive
  // has lost its structured sections, references and chips — disclose that
  // explicitly instead of presenting a degraded artifact as the original.
  const restoredTextOnly = Boolean(
    (message as { restoredTextOnly?: boolean }).restoredTextOnly
  );
  return (
    <CodeReferencesUiContext.Provider value={codeUiCtx}>
      {restoredTextOnly ? (
        <div className="space-y-2">
          {body}
          <p
            data-testid="restored-text-only-notice"
            className="text-[11px] leading-4 text-text-tertiary italic"
          >
            Restored from the text archive — the original formatted review
            (tables, references, follow-up chips) is not available on this
            device.
          </p>
        </div>
      ) : (
        body
      )}
    </CodeReferencesUiContext.Provider>
  );
}

export function useAssistantCardDerivations(
  message: any,
  normalizedEvents: NormalizedThinkingEvent[],
) {
  const isAssistant = message.role === 'assistant';
  const rm = message.responseMetadata as Record<string, unknown> | null | undefined;

  const suppressClinicalRefs =
    isAssistant && responseMetaPick<boolean>(rm, 'suppress_clinical_references', 'suppressClinicalReferences') === true;

  const clinicalRefSources = React.useMemo(() => {
    if (!isAssistant || suppressClinicalRefs) return [];
    const rag = responseMetaPick<unknown>(rm, 'rag_sources', 'ragSources');
    if (!Array.isArray(rag)) return [];
    return filterClinicalReferenceSources(rag as RagSourcePayload[]);
  }, [isAssistant, message.responseMetadata, suppressClinicalRefs]);

  // Option A CMA per-branch RAG: the citation-free reference/tooltip contract.
  // Present only on the per-branch CMA path; when present it supersedes the
  // legacy numbered RagSourcesPanel at the render sites below.
  const guidelineEvidence = React.useMemo<GuidelineEvidencePayload | undefined>(() => {
    if (!isAssistant || suppressClinicalRefs) return undefined;
    const ge = responseMetaPick<GuidelineEvidencePayload>(rm, 'guideline_evidence', 'guidelineEvidence');
    if (!ge || typeof ge !== 'object') return undefined;
    const hasRefs = Array.isArray(ge.references) && ge.references.length > 0;
    const hasCats =
      Array.isArray(ge.categories) && ge.categories.some((c) => String(c?.answer ?? '').trim());
    return hasRefs || hasCats ? ge : undefined;
  }, [isAssistant, message.responseMetadata, suppressClinicalRefs]);

  // #18: whether the actual response content (narrative answer + any
  // structured-section text) carries a resolvable "[N]" marker - governs the
  // legacy RagSourcesPanel's own title/numbering below (see
  // hasInlineCitationMarkerText). Independent of guidelineEvidence, which is
  // a separate, already citation-free panel.
  const hasInlineCitationMarkers = React.useMemo(() => {
    if (!isAssistant) return false;
    const narrative = message.answer ? String(message.answer) : message.content ? String(message.content) : '';
    const structured = (rm?.structured ?? undefined) as { sections?: unknown } | undefined;
    const structuredText = collectStructuredSectionText(structured?.sections);
    return hasInlineCitationMarkerText(`${narrative}\n${structuredText}`);
  }, [isAssistant, message.answer, message.content, message.responseMetadata]);

  const specialtyOutputs = React.useMemo(() => {
    if (!isAssistant) return [];
    const raw = responseMetaPick<unknown>(rm, 'specialty_outputs', 'specialtyOutputs');
    if (!Array.isArray(raw)) return [];
    return raw.filter((item): item is SpecialtyResultPayload => !!item && typeof item === 'object');
  }, [isAssistant, message.responseMetadata]);

  const specialtyOutputsFromEvents = React.useMemo(() => {
    if (!isAssistant || specialtyOutputs.length > 0) return [];
    return fallbackSpecialtyOutputsFromEvents(normalizedEvents);
  }, [isAssistant, normalizedEvents, specialtyOutputs.length]);

  const effectiveSpecialtyOutputs = specialtyOutputs.length > 0 ? specialtyOutputs : specialtyOutputsFromEvents;
  const hasSpecialtyOutputs = effectiveSpecialtyOutputs.length > 0;
  const routerOverview = String(responseMetaPick(rm, 'router_overview', 'routerOverview') ?? '').trim();
  const engagedFromMetaRaw = responseMetaPick<unknown[]>(rm, 'engaged_specialties', 'engagedSpecialties');
  const engagedFromMeta =
    Array.isArray(engagedFromMetaRaw) && engagedFromMetaRaw.length > 0
      ? engagedFromMetaRaw.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
      : [];
  const engagedSpecialties =
    engagedFromMeta.length > 0
      ? engagedFromMeta
      : effectiveSpecialtyOutputs.map((output) => String(output.specialty ?? '').trim()).filter(Boolean);
  const effectiveEngagedSpecialties = engagedSpecialties.length > 0
    ? engagedSpecialties
    : fallbackEngagedSpecialtiesFromEvents(normalizedEvents);
  const effectiveRouterOverview = buildClinicalOverview(
    routerOverview || fallbackRouterOverviewFromEvents(normalizedEvents, effectiveSpecialtyOutputs),
    effectiveSpecialtyOutputs,
    effectiveEngagedSpecialties,
  );
  const specialtyOutputMode = String(responseMetaPick(rm, 'specialty_output_mode', 'specialtyOutputMode') ?? '').trim();
  const specialtyRouting = responseMetaPick(rm, 'specialty_routing', 'specialtyRouting');
  const isSpecialtyResponse =
    hasSpecialtyOutputs ||
    effectiveEngagedSpecialties.length > 0 ||
    specialtyOutputMode.length > 0 ||
    !!specialtyRouting;
  const skippedRaw = responseMetaPick<unknown[]>(
    rm,
    'skipped_specialties_without_rag',
    'skippedSpecialtiesWithoutRag',
  );
  const skippedSpecialtiesWithoutRag = Array.isArray(skippedRaw)
    ? skippedRaw.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [];
  const suggestedRaw = responseMetaPick<unknown[]>(rm, 'suggested_next_questions', 'suggestedNextQuestions');
  const suggestedNextQuestions = Array.isArray(suggestedRaw)
    ? suggestedRaw.map((item: unknown) => String(item ?? '').trim()).filter(Boolean).slice(0, 3)
    : [];

  return {
    suppressClinicalRefs,
    clinicalRefSources,
    guidelineEvidence,
    hasInlineCitationMarkers,
    effectiveSpecialtyOutputs,
    hasSpecialtyOutputs,
    effectiveEngagedSpecialties,
    effectiveRouterOverview,
    isSpecialtyResponse,
    skippedSpecialtiesWithoutRag,
    suggestedNextQuestions,
  };
}

export type AssistantCardDerivations = ReturnType<typeof useAssistantCardDerivations>;

/** Main assistant reply card — same markup as clinician workspace transcript. */
export function AssistantClinicalAnswerCard({
  message,
  answerScopeId,
  derivations,
  onSendSuggestion,
  clinicalQualityRerunUserText,
  onClinicalQualityRerun,
  onRetryCommand,
  showEvidence,
  readOnlySuggestedQuestions,
  polishedChrome,
  preferTranscriptMarkdown = false,
}: {
  message: any;
  answerScopeId: string;
  derivations: AssistantCardDerivations;
  onSendSuggestion?: (msg: string) => void;
  clinicalQualityRerunUserText?: string;
  onClinicalQualityRerun?: (userText: string) => void;
  onRetryCommand?: (command: string) => void;
  showEvidence?: boolean;
  readOnlySuggestedQuestions?: boolean;
  polishedChrome?: boolean;
  preferTranscriptMarkdown?: boolean;
}) {
  const {
    suppressClinicalRefs,
    clinicalRefSources,
    guidelineEvidence,
    hasInlineCitationMarkers,
    effectiveSpecialtyOutputs,
    hasSpecialtyOutputs,
    effectiveEngagedSpecialties,
    effectiveRouterOverview,
    isSpecialtyResponse,
    skippedSpecialtiesWithoutRag,
    suggestedNextQuestions,
  } = derivations;

  const rm = (message.responseMetadata ?? {}) as Record<string, unknown>;
  const processingMsRaw = responseMetaPick<number | string>(rm, 'processing_time_ms', 'processingTimeMs');
  const codeRefsForFooterRaw = responseMetaPick<unknown>(rm, 'code_references', 'codeReferences');
  const codeRefsFooterCount = Array.isArray(codeRefsForFooterRaw) ? codeRefsForFooterRaw.length : 0;
  const executionFlow = responseMetaPick<string>(rm, 'execution_flow', 'executionFlow');
  const responseContractForFooter = responseMetaPick<string>(rm, 'agentic_core_adk_response_contract', 'responseContract') ?? message.responseMetadata?.agentic_core_adk_response_contract;
  const intentTypeForFooter = responseMetaPick<string>(rm, 'intent_type', 'intentType');

  const conciseSpecialtyTranscript = shouldPreferSpecialtyTranscriptMarkdown(message);

  const outerTone = polishedChrome
    ? 'min-w-0 w-full rounded border border-border/80 bg-surface-raised shadow-lg ring-1 ring-border/10 overflow-hidden'
    : 'min-w-0 w-full bg-surface-raised rounded-md border border-border overflow-hidden';

  return (
    <div className={outerTone}>
      <div className="min-w-0 space-y-0 p-4 sm:p-6">
        {message.isStreaming && message.streamPhase === 'thinking' && !message.answer && (
          <div className="space-y-3 py-1">
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <span className="thinking-pulse-dot" />
              <span className="font-medium">Analyzing your request…</span>
            </div>
            <div className="space-y-2 animate-pulse">
              <div className="h-3 bg-surface-sunken rounded-full w-3/4" />
              <div className="h-3 bg-surface-sunken rounded-full w-1/2" />
              <div className="h-3 bg-surface-sunken rounded-full w-5/6" />
            </div>
          </div>
        )}

        {message.isStreaming && message.streamPhase === 'final' && !message.answer && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              <span className="font-medium">Writing response…</span>
            </div>
            <div className="space-y-2 animate-pulse">
              <div className="h-3 bg-surface-sunken rounded-full w-5/6" />
              <div className="h-3 bg-surface-sunken rounded-full w-2/3" />
            </div>
          </div>
        )}
        {message.isStreaming && message.streamPhase === 'final' && message.answer && (
          <div className="mb-1 flex items-center gap-2 text-xs text-text-tertiary">
            <Loader2 className="w-3 h-3 animate-spin text-primary" />
            <span className="font-medium">Writing…</span>
          </div>
        )}

        <StructuredOrMarkdown
          message={message}
          answerScopeId={answerScopeId}
          preferTranscriptMarkdown={preferTranscriptMarkdown}
        />
        {/* specialty-multi-agent renderer family (RouterOverviewPanel /
            SpecialtyDetailedResultsPanel) stays outside the unified
            ResponseFrame — legacy/out-of-scope for ticket #101 (WS-5). */}
        {!message.isStreaming && isSpecialtyResponse && !conciseSpecialtyTranscript ? (
          <RouterOverviewPanel overview={effectiveRouterOverview} specialties={effectiveEngagedSpecialties} />
        ) : null}
        {!message.isStreaming && isSpecialtyResponse && !conciseSpecialtyTranscript ? (
          <SpecialtyDetailedResultsPanel
            outputs={effectiveSpecialtyOutputs}
            skippedSpecialties={skippedSpecialtiesWithoutRag}
          />
        ) : null}
        <ResponseFrame
          references={
            // The citation-free GuidelineEvidencePanel (clickable references,
            // each opening the real retrieved passage) is the current design
            // and always supersedes the legacy numbered RagSourcesPanel when
            // both are present in the payload. RagSourcesPanel is a fallback
            // for routes that never populate guideline_evidence at all.
            !message.isStreaming && !suppressClinicalRefs && guidelineEvidence ? (
              <GuidelineEvidencePanel evidence={guidelineEvidence} />
            ) : !message.isStreaming &&
              !isSpecialtyResponse &&
              !hasSpecialtyOutputs &&
              !suppressClinicalRefs &&
              clinicalRefSources.length > 0 ? (
              <RagSourcesPanel sources={clinicalRefSources} hasInlineCitations={hasInlineCitationMarkers} />
            ) : null
          }
          chips={
            !message.isStreaming ? (
              <SuggestedQuestionsPanel
                suggestions={suggestedNextQuestions}
                onSelect={readOnlySuggestedQuestions ? undefined : onSendSuggestion}
                readOnly={readOnlySuggestedQuestions}
              />
            ) : null
          }
          footer={
            <>
              {!message.isStreaming &&
                message.responseMetadata &&
                clinicalQualityVisible(message.responseMetadata) && (
                  <div className="mt-4">
                    <ClinicalQualityReviewPanel
                      metadata={message.responseMetadata}
                      clinicalQualityRerunUserText={clinicalQualityRerunUserText}
                      onClinicalQualityRerun={onClinicalQualityRerun}
                    />
                  </div>
                )}
              {message.widget?.data?.filename && (message.widget.data.format === 'csv' || message.widget.data.format === 'pdf') && (
                <div className="mt-4">
                  <a
                    href={`${(import.meta.env.VITE_MARCH_API_URL ?? '').toString().replace(/\/$/, '')}/api/exports/${encodeURIComponent(String(message.widget.data.filename))}`}
                    download={String(message.widget.data.filename)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-surface-sunken/30 hover:bg-surface-sunken/50 text-sm font-medium transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    {message.widget.data.format === 'pdf' ? 'Download PDF' : 'Download CSV'}
                  </a>
                </div>
              )}
              {!message.isStreaming && message.responseMetadata && !isSpecialtyResponse && (
                <div className="mt-5 space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-[11px] text-text-tertiary/70 pt-2 border-t border-border/30">
                    {processingMsRaw != null && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDuration(Number(processingMsRaw))}</span>
                      </div>
                    )}
                    {executionFlow && (
                      <>
                        <span className="text-border/30">·</span>
                        <span>{flowLabel(executionFlow, responseContractForFooter, intentTypeForFooter)}</span>
                      </>
                    )}
                    {!hasSpecialtyOutputs && clinicalRefSources.length > 0 && (
                      <>
                        <span className="text-border/30">·</span>
                        <span className="text-brand-text font-medium">References</span>
                      </>
                    )}
                    {codeRefsFooterCount > 0 && (
                      <>
                        <span className="text-border/30">·</span>
                        <span className="text-(--chart-categorical-1) font-medium">UMLS</span>
                      </>
                    )}
                    {(message.responseMetadata.clinical_quality_gate_status === 'passed' ||
                (message.responseMetadata.clinical_quality_gate_satisfied === true &&
                  message.responseMetadata.clinical_quality_layer_authoritative === true)) && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-positive-text font-medium cursor-help">
                        <ShieldCheck className="w-3 h-3 shrink-0" />
                        Clinical quality checked
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left border border-border shadow-lg">
                      <p className="text-xs text-text-tertiary">
                        Terminal clinical-quality review passed (rubric floors met).
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {message.responseMetadata.clinical_quality_gate_status === 'provisional' && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-attention-text font-medium cursor-help">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        Provisional quality
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left border border-border shadow-lg">
                      <p className="text-xs text-text-tertiary">
                        Some rubric targets were not met; confirm details in the source record before clinical use.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {message.responseMetadata.clinical_quality_gate_status === 'failed' && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-negative-text font-medium cursor-help">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        Quality gate failed
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm text-left border border-border shadow-lg">
                      <p className="text-xs text-text-tertiary">
                        Hard safety or factuality floors were not met. Do not rely on this reply for care without
                        independent verification.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {message.responseMetadata.high_risk_user_prompt === true && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-attention-text font-medium cursor-help">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        Sensitive prompt
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm text-left border border-border shadow-lg">
                      <p className="text-[10px] uppercase tracking-wide text-text-tertiary mb-1.5">Scope policy</p>
                      <p className="text-xs leading-relaxed">
                        {(message.responseMetadata.high_risk_hits?.length
                          ? message.responseMetadata.high_risk_hits.join('; ')
                          : 'This user message matched high-caution patterns. Review the reply carefully.')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        )}
            </>
          }
        />

        {showEvidence && message.evidence && !message.responseMetadata && (
          <div className="text-[11px] sm:text-xs text-text-tertiary flex items-center gap-2 sm:gap-3 flex-wrap pb-2 border-b border-border mb-3">
            {message.evidence.sources && (
              <div className="flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5" />
                <span>{message.evidence.sources} sources</span>
              </div>
            )}
            {message.evidence.generated && (
              <>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Generated {String(message.evidence.generated)}</span>
                </div>
              </>
            )}
            {message.evidence.runId && (
              <>
                <span>•</span>
                <span className="font-mono text-[9px] sm:text-xs bg-surface-sunken px-2 py-1 rounded">ID: {String(message.evidence.runId).slice(0, 8)}</span>
              </>
            )}
          </div>
        )}

        {message.suggestions && (
          <div className="text-sm text-text-tertiary bg-primary/8 border border-primary/20 rounded-lg px-4 py-3 flex items-start gap-3">
            <Zap className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
            <span><span className="font-medium text-foreground/90">Next:</span> {message.suggestions}</span>
          </div>
        )}

        {message.retryCommand && onRetryCommand && (
          <div className="mt-4 pt-4 border-t border-border/50">
            <button
              type="button"
              onClick={() => onRetryCommand(message.retryCommand)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-linear-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-xl transition-all shadow-sm hover:shadow-md"
            >
              <RotateCw className="w-4 h-4" />
              Retry Command
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Message({
  message,
  showEvidence,
  onRetryCommand,
  onSendSuggestion,
  clinicalQualityRerunUserText,
  onClinicalQualityRerun,
  onSavePrompt,
  onRemoveSavedPrompt,
  isPromptSaved,
}: MessageProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState(false);
  const messageRef = useRef<HTMLDivElement>(null);
  const answerScopeFallbackId = useId();
  const answerScopeId = React.useMemo(
    () =>
      [
        message.id != null ? String(message.id) : null,
        message.evidence?.runId ? String(message.evidence.runId).slice(0, 8) : null,
        answerScopeFallbackId.replace(/:/g, '-'),
      ]
        .filter(Boolean)
        .join('-'),
    [message.id, message.evidence?.runId, answerScopeFallbackId],
  );

  const normalizedEvents: NormalizedThinkingEvent[] = React.useMemo(() => {
    return (message._normalizedEvents as NormalizedThinkingEvent[]) ?? [];
  }, [message._normalizedEvents]);

  const hasThinkingData = normalizedEvents.length > 0 || (message.isStreaming && message.streamPhase === 'thinking');

  // CMA-OPT-71: how many progress lines the answer area is currently showing.
  // Same builder the surface itself uses, so the two can never disagree about
  // whether the area is empty.
  const cmaProgressLineCount = React.useMemo(
    () => (message.isStreaming ? buildCmaProgressLines(normalizedEvents).length : 0),
    [message.isStreaming, normalizedEvents],
  );

  const executionStatus = React.useMemo(() => {
    return deriveExecutionStatus(
      normalizedEvents,
      !!message.isStreaming,
      !!message.thinkingComplete,
    );
  }, [normalizedEvents, message.isStreaming, message.thinkingComplete]);

  const thinkingMetrics = React.useMemo(() => {
    return computeMetrics(
      normalizedEvents,
      message._firstFinalTokenAt as string | undefined,
      !message.isStreaming ? new Date().toISOString() : undefined,
    );
  }, [normalizedEvents, message._firstFinalTokenAt, message.isStreaming]);

  const warningCount = normalizedEvents.filter(
    (e) => e.severity === 'warning' || e.severity === 'error' || e.severity === 'critical',
  ).length;

  const toolsUsed = React.useMemo(() => {
    if (message.responseMetadata?.intent_type === 'chart_briefing') {
      return [];
    }
    const metadataTools = Array.isArray(message.responseMetadata?.tools_used)
      ? message.responseMetadata.tools_used
          .map((tool: string) => String(tool || '').trim())
          .filter(Boolean)
      : [];

    const eventTools = normalizedEvents
      .filter((e) => e.toolName && e.status !== 'running')
      .map((e) => String(e.toolName || '').trim())
      .filter(Boolean);

    return Array.from(new Set([...metadataTools, ...eventTools]));
  }, [message.responseMetadata?.intent_type, message.responseMetadata?.tools_used, normalizedEvents]);

  const toolsUsedCount = toolsUsed.length;

  const currentStepName = React.useMemo(() => {
    const running = normalizedEvents.filter((e) => e.status === 'running');
    return running.length > 0 ? running[running.length - 1].title : undefined;
  }, [normalizedEvents]);

  const cardDerivations = useAssistantCardDerivations(message, normalizedEvents);
  const {
    suppressClinicalRefs,
    clinicalRefSources,
    guidelineEvidence,
    hasInlineCitationMarkers,
    effectiveSpecialtyOutputs,
    hasSpecialtyOutputs,
    effectiveEngagedSpecialties,
    effectiveRouterOverview,
    isSpecialtyResponse,
    skippedSpecialtiesWithoutRag,
    suggestedNextQuestions,
  } = cardDerivations;

  // Helper function to convert widget data to comprehensive HTML
  function widgetToHTML(widget: any): string {
    if (!widget || !widget.data) return '';
    
    const data = widget.data;
    let html = '';
    
    switch (widget.type) {
      case 'W_SUMMARY_CLINICAL':
        html += safeHtml`<h3>Clinical Summary</h3>`;
        if (data.summary) {
          html += safeHtml`<p>${escapeHtmlWithBreaks(data.summary)}</p>`;
        }
        if (data.keyPoints && Array.isArray(data.keyPoints)) {
          html += safeHtml`<h4>Key Points</h4><ul>`;
          data.keyPoints.forEach((p: string) => {
            html += safeHtml`<li>${p}</li>`;
          });
          html += safeHtml`</ul>`;
        }
        break;
        
      case 'W_RISK_PROFILE':
        html += safeHtml`<h3>Risk Profile</h3>`;
        html += safeHtml`<table><tr><th>Category</th><th>Level</th><th>Description</th></tr>`;
        html += safeHtml`<tr><td><strong>Overall Risk</strong></td><td>${data.overall || data.level || ''}</td><td>Overall risk assessment</td></tr>`;
        if (data.categories && Array.isArray(data.categories)) {
          data.categories.forEach((cat: any) => {
            html += safeHtml`<tr><td>${cat.name || ''}</td><td>${cat.level || ''}</td><td>${cat.description || ''}</td></tr>`;
          });
        }
        if (data.factors && Array.isArray(data.factors)) {
          data.factors.forEach((f: any) => {
            html += safeHtml`<tr><td>${f.name || ''}</td><td>${f.value || ''}</td><td>${f.description || ''}</td></tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_RISK_DRIVERS':
        html += safeHtml`<h3>Risk Factors</h3>`;
        html += safeHtml`<table><tr><th>Factor</th><th>Contribution</th><th>Trend</th><th>Current</th><th>Baseline</th></tr>`;
        if (data.drivers && Array.isArray(data.drivers)) {
          data.drivers.forEach((driver: any) => {
            html += safeHtml`<tr>
              <td><strong>${driver.name || ''}</strong><br><small>${driver.description || ''}</small></td>
              <td>${driver.contribution || driver.value || ''}%</td>
              <td>${driver.trend === 'up' ? '↑' : driver.trend === 'down' ? '↓' : '—'}</td>
              <td>${driver.values?.current || driver.current || ''}</td>
              <td>${driver.values?.baseline || driver.baseline || ''}</td>
            </tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_ALERT_INBOX':
        html += safeHtml`<h3>Active Alerts (${data.alerts?.length || 0})</h3>`;
        html += safeHtml`<table><tr><th>ID</th><th>Title</th><th>Severity</th><th>Description</th><th>Category</th><th>Timestamp</th></tr>`;
        if (data.alerts && Array.isArray(data.alerts)) {
          data.alerts.forEach((alert: any) => {
            html += safeHtml`<tr>
              <td><code>${alert.id || ''}</code></td>
              <td><strong>${alert.title || ''}</strong></td>
              <td>${alert.severity || ''}</td>
              <td>${alert.description || alert.message || ''}</td>
              <td>${alert.category || ''}</td>
              <td>${alert.timestamp || ''}</td>
            </tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_ALERT_DETAIL':
        html += safeHtml`<h3>Alert Details</h3>`;
        html += safeHtml`<table><tr><th>Property</th><th>Value</th></tr>`;
        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'recommendations') {
            html += safeHtml`<tr><td><strong>${key}</strong></td><td>${typeof value === 'object' ? JSON.stringify(value) : value}</td></tr>`;
          }
        });
        if (data.recommendations && Array.isArray(data.recommendations)) {
          html += safeHtml`<tr><td><strong>Recommendations</strong></td><td><ul>`;
          data.recommendations.forEach((r: string) => {
            html += safeHtml`<li>${r}</li>`;
          });
          html += safeHtml`</ul></td></tr>`;
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_ASSESS_OVERVIEW':
        html += safeHtml`<h3>Assessment Overview</h3>`;
        html += safeHtml`<table><tr><th>Metric</th><th>Score</th><th>Trend</th></tr>`;
        html += safeHtml`<tr><td><strong>Overall Score</strong></td><td>${data.overallScore || ''}/100</td><td>${data.trend || ''} ${data.trendValue || ''}</td></tr>`;
        html += safeHtml`<tr><td>Status</td><td colspan="2">${data.status || ''}</td></tr>`;
        html += safeHtml`<tr><td>ID</td><td colspan="2"><code>${data.id || ''}</code></td></tr>`;
        html += safeHtml`<tr><td>Timestamp</td><td colspan="2">${data.timestamp || ''}</td></tr>`;
        if (data.metrics && Array.isArray(data.metrics)) {
          data.metrics.forEach((metric: any) => {
            html += safeHtml`<tr>
              <td>${metric.name || ''}</td>
              <td>${metric.score || ''}/100</td>
              <td>${metric.trend || ''}</td>
            </tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_ASSESS_DETAIL':
        html += safeHtml`<h3>Assessment Detail</h3>`;
        if (data.summary) {
          html += safeHtml`<p><strong>Summary:</strong> ${data.summary}</p>`;
        }
        if (data.findings && Array.isArray(data.findings)) {
          html += safeHtml`<table><tr><th>Finding</th><th>Value</th></tr>`;
          data.findings.forEach((f: any) => {
            html += safeHtml`<tr><td>${f.label || f.name || ''}</td><td>${f.value || ''}</td></tr>`;
          });
          html += safeHtml`</table>`;
        }
        // Add all other properties
        html += safeHtml`<h4>All Parameters</h4><table><tr><th>Parameter</th><th>Value</th></tr>`;
        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'summary' && key !== 'findings') {
            html += safeHtml`<tr><td><strong>${key}</strong></td><td>${typeof value === 'object' ? JSON.stringify(value, null, 2) : value}</td></tr>`;
          }
        });
        html += safeHtml`</table>`;
        break;
        
      case 'W_ASSESS_REDFLAGS':
        html += safeHtml`<h3>Red Flags</h3>`;
        html += safeHtml`<table><tr><th>ID</th><th>Title</th><th>Description</th><th>Metric</th><th>Timestamp</th><th>Requires Acknowledgement</th></tr>`;
        if (data.flags && Array.isArray(data.flags)) {
          data.flags.forEach((flag: any) => {
            html += safeHtml`<tr>
              <td><code>${flag.id || ''}</code></td>
              <td><strong>${flag.title || flag.label || ''}</strong></td>
              <td>${flag.description || ''}</td>
              <td>${flag.metric || ''}</td>
              <td>${flag.timestamp || ''}</td>
              <td>${flag.requiresAck ? 'Yes' : 'No'}</td>
            </tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_TWIN_SNAPSHOT':
        html += safeHtml`<h3>Digital Twin Snapshot</h3>`;
        html += safeHtml`<table><tr><th>Property</th><th>Value</th></tr>`;
        html += safeHtml`<tr><td><strong>Status</strong></td><td>${data.status || ''}</td></tr>`;
        if (data.metrics && typeof data.metrics === 'object') {
          Object.entries(data.metrics).forEach(([key, value]) => {
            html += safeHtml`<tr><td>${key}</td><td>${value}</td></tr>`;
          });
        }
        // Add all other properties
        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'status' && key !== 'metrics') {
            html += safeHtml`<tr><td><strong>${key}</strong></td><td>${typeof value === 'object' ? JSON.stringify(value, null, 2) : value}</td></tr>`;
          }
        });
        html += safeHtml`</table>`;
        break;
        
      case 'W_TWIN_SIMULATION':
        html += safeHtml`<h3>Simulation Result</h3>`;
        html += safeHtml`<table><tr><th>Metric</th><th>Value</th></tr>`;
        html += safeHtml`<tr><td>Baseline Risk</td><td>${data.baselineRisk || ''}%</td></tr>`;
        html += safeHtml`<tr><td>Scenario Risk</td><td>${data.scenarioRisk || ''}%</td></tr>`;
        html += safeHtml`<tr><td>Delta</td><td>${data.delta || ''}%</td></tr>`;
        html += safeHtml`<tr><td>Confidence</td><td>${data.confidence || ''}%</td></tr>`;
        html += safeHtml`<tr><td>Completeness</td><td>${data.completeness || ''}%</td></tr>`;
        if (data.horizon) html += safeHtml`<tr><td>Horizon</td><td>${data.horizon}</td></tr>`;
        if (data.doseAdjustment) {
          html += safeHtml`<tr><td colspan="2"><strong>Dose Adjustment:</strong> ${data.doseAdjustment}</td></tr>`;
        }
        if (data.sources && Array.isArray(data.sources)) {
          html += safeHtml`<tr><td colspan="2"><strong>Sources:</strong> ${data.sources.join(', ')}</td></tr>`;
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_SIMULATION_HISTORY':
        html += safeHtml`<h3>Simulation History</h3>`;
        html += safeHtml`<table><tr><th>SIM-ID</th><th>Scenario</th><th>Horizon</th><th>Key Delta</th><th>Timestamp</th></tr>`;
        if (data.simulations && Array.isArray(data.simulations)) {
          data.simulations.forEach((sim: any) => {
            html += safeHtml`<tr>
              <td><code>${sim.id || ''}</code></td>
              <td>${sim.scenario || ''}</td>
              <td>${sim.horizon || ''}</td>
              <td>${sim.delta || ''}</td>
              <td>${sim.timestamp || ''}</td>
            </tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_NOTE_DRAFT':
        html += safeHtml`<h3>Clinical Note Draft</h3>`;
        html += safeHtml`<table><tr><th>Property</th><th>Value</th></tr>`;
        html += safeHtml`<tr><td>Type</td><td>${data.type || ''}</td></tr>`;
        html += safeHtml`<tr><td>Patient</td><td>${data.patientName || ''}</td></tr>`;
        html += safeHtml`<tr><td>Date</td><td>${data.date || ''}</td></tr>`;
        if (data.noteId) html += safeHtml`<tr><td>Note ID</td><td><code>${data.noteId}</code></td></tr>`;
        html += safeHtml`</table>`;
        if (data.sections && Array.isArray(data.sections)) {
          html += safeHtml`<h4>Sections</h4>`;
          data.sections.forEach((section: any, idx: number) => {
            html += safeHtml`<div style="margin: 15px 0; padding: 10px; border-left: 3px solid #3b82f6;">`;
            html += safeHtml`<h5>${section.heading || `Section ${idx + 1}`}</h5>`;
            html += safeHtml`<p style="white-space: pre-wrap;">${section.content || ''}</p>`;
            html += safeHtml`</div>`;
          });
        }
        break;
        
      case 'W_PLAN_DRAFT':
        html += safeHtml`<h3>Treatment Plan Draft</h3>`;
        html += safeHtml`<table><tr><th>Property</th><th>Value</th></tr>`;
        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'phases') {
            html += safeHtml`<tr><td><strong>${key}</strong></td><td>${typeof value === 'object' ? JSON.stringify(value) : value}</td></tr>`;
          }
        });
        html += safeHtml`</table>`;
        if (data.phases && Array.isArray(data.phases)) {
          html += safeHtml`<h4>Phases</h4>`;
          data.phases.forEach((phase: any, idx: number) => {
            html += safeHtml`<div style="margin: 15px 0; padding: 10px; border-left: 3px solid #10b981;">`;
            html += safeHtml`<h5>${phase.name || `Phase ${idx + 1}`}</h5>`;
            if (phase.description) html += safeHtml`<p>${phase.description}</p>`;
            if (phase.duration) html += safeHtml`<p><strong>Duration:</strong> ${phase.duration}</p>`;
            if (phase.medications && Array.isArray(phase.medications)) {
              html += safeHtml`<ul>`;
              phase.medications.forEach((med: any) => {
                html += safeHtml`<li>${typeof med === 'string' ? med : JSON.stringify(med)}</li>`;
              });
              html += safeHtml`</ul>`;
            }
            html += safeHtml`</div>`;
          });
        }
        break;
        
      case 'W_PLAN_DIFF':
        html += safeHtml`<h3>Plan Changes</h3>`;
        html += safeHtml`<table><tr><th>Type</th><th>Description</th><th>Details</th></tr>`;
        if (data.changes && Array.isArray(data.changes)) {
          data.changes.forEach((change: any) => {
            html += safeHtml`<tr>
              <td>${change.type || ''}</td>
              <td>${change.description || change.detail || ''}</td>
              <td>${typeof change === 'object' ? JSON.stringify(change, null, 2) : change}</td>
            </tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_AUDIT':
        html += safeHtml`<h3>Action Audit</h3>`;
        html += safeHtml`<table><tr><th>Property</th><th>Value</th></tr>`;
        html += safeHtml`<tr><td>Action</td><td><strong>${data.action || ''}</strong></td></tr>`;
        if (data.target) html += safeHtml`<tr><td>Target</td><td>${data.target}</td></tr>`;
        if (data.user) html += safeHtml`<tr><td>User</td><td>${data.user}</td></tr>`;
        if (data.timestamp) html += safeHtml`<tr><td>Timestamp</td><td>${data.timestamp}</td></tr>`;
        if (data.auditId) html += safeHtml`<tr><td>Audit ID</td><td><code>${data.auditId}</code></td></tr>`;
        if (data.details && Array.isArray(data.details)) {
          data.details.forEach((detail: any) => {
            html += safeHtml`<tr><td>${detail.label || ''}</td><td>${detail.value || ''}</td></tr>`;
          });
        }
        html += safeHtml`</table>`;
        break;
        
      case 'W_FILE_PDF':
        html += safeHtml`<h3>PDF Document</h3>`;
        html += safeHtml`<table><tr><th>Property</th><th>Value</th></tr>`;
        Object.entries(data).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) return;
          html += safeHtml`<tr><td><strong>${key}</strong></td><td>${String(value ?? '')}</td></tr>`;
        });
        html += safeHtml`</table>`;
        break;
        
      case 'W_CHART_LINE':
        html += safeHtml`<h3>Chart: ${data.title || 'Data Chart'}</h3>`;
        if (data.description) html += safeHtml`<p>${data.description}</p>`;
        if (data.points && Array.isArray(data.points)) {
          html += safeHtml`<table><tr><th>Date</th><th>Value</th></tr>`;
          data.points.forEach((point: any) => {
            html += safeHtml`<tr>
              <td>${point.date || point.x || ''}</td>
              <td>${point.value || point.y || ''}</td>
            </tr>`;
          });
          html += safeHtml`</table>`;
        }
        break;
      case 'W_FHIR_PATIENT':
        html += safeHtml`<h3>Patient record</h3>`;
        if (data.configured && data.found) {
          if (data.name) html += safeHtml`<p><strong>Name:</strong> ${data.name}</p>`;
          if (data.fhirId) html += safeHtml`<p><strong>Patient ID:</strong> <code>${data.fhirId}</code></p>`;
          if (data.birthDate) html += safeHtml`<p><strong>Birth date:</strong> ${data.birthDate}</p>`;
          if (data.gender) html += safeHtml`<p><strong>Gender:</strong> ${data.gender}</p>`;
          html += safeHtml`<p><strong>Patient ID:</strong> <code>${data.patientId}</code></p>`;
        } else {
          html += safeHtml`<p>${data.message ?? 'Patient record is not available.'}</p>`;
        }
        break;
        
      default:
        // For any other widget type, create a comprehensive table of all data
        html += safeHtml`<h3>${widget.type.replace(/_/g, ' ')}</h3>`;
        html += safeHtml`<table><tr><th>Parameter</th><th>Value</th></tr>`;
        const flattenObject = (obj: any, prefix = ''): Array<[string, any]> => {
          const result: Array<[string, any]> = [];
          for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
              const newKey = prefix ? `${prefix}.${key}` : key;
              if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                result.push(...flattenObject(obj[key], newKey));
              } else if (Array.isArray(obj[key])) {
                obj[key].forEach((item: any, idx: number) => {
                  if (typeof item === 'object' && item !== null) {
                    result.push(...flattenObject(item, `${newKey}[${idx}]`));
                  } else {
                    result.push([`${newKey}[${idx}]`, item]);
                  }
                });
              } else {
                result.push([newKey, obj[key]]);
              }
            }
          }
          return result;
        };
        flattenObject(data).forEach(([key, value]) => {
          const display = Array.isArray(value) ? value.join(', ') : String(value ?? '');
          html += safeHtml`<tr><td><strong>${key}</strong></td><td>${display}</td></tr>`;
        });
        html += safeHtml`</table>`;
    }
    
    return html;
  }

  const handleDownload = async () => {
    // Get the message element
    const messageElement = messageRef.current;
    if (!messageElement) return;

    // Create comprehensive HTML content for PDF.
    //
    // SEC-I1: every `${...}` below (and in widgetToHTML) goes through the `safeHtml`
    // tagged template, which escapes interpolations by default — the document is
    // built from model-generated text and rendered into a window that inherits this
    // origin (see utils/htmlExport.ts). The `default-src 'none'` meta CSP is the
    // second layer: this export needs no scripts, no network and no images, so even
    // a future escaping mistake cannot execute. `style-src 'unsafe-inline'` is
    // required for the <style> block and the inline style attributes below.
    let htmlContent = safeHtml`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'">
          <title>Message Export - ${new Date().toLocaleDateString()}</title>
          <style>
            @media print {
              body { margin: 0; padding: 15px; }
              .page-break { page-break-after: always; }
            }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              padding: 20px; 
              color: #333; 
              max-width: 800px;
              margin: 0 auto;
            }
            h1 { font-size: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            h2 { font-size: 16px; margin-top: 25px; margin-bottom: 12px; color: #555; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
            h3 { font-size: 14px; margin-top: 20px; margin-bottom: 10px; color: #666; }
            h4 { font-size: 13px; margin-top: 15px; margin-bottom: 8px; color: #777; }
            h5 { font-size: 12px; margin-top: 12px; margin-bottom: 6px; color: #888; }
            p { margin: 10px 0; line-height: 1.6; }
            .evidence { 
              font-size: 12px; 
              color: #666; 
              margin-top: 20px; 
              padding: 15px; 
              border-top: 2px solid #ddd; 
              background: #f9f9f9;
            }
            .widget { 
              margin: 25px 0; 
              padding: 20px; 
              border: 1px solid #ddd; 
              border-radius: 8px; 
              background: #f9f9f9; 
              page-break-inside: avoid;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 15px 0; 
              font-size: 12px;
              page-break-inside: avoid;
            }
            th, td { 
              padding: 10px; 
              text-align: left; 
              border: 1px solid #ddd; 
            }
            th { 
              background: #f0f0f0; 
              font-weight: 600; 
              color: #333;
            }
            tr:nth-child(even) { background: #fafafa; }
            code { 
              background: #f4f4f4; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-family: 'Monaco', 'Courier New', monospace;
              font-size: 11px;
            }
            ul, ol { margin: 10px 0; padding-left: 25px; }
            li { margin: 5px 0; line-height: 1.5; }
            pre { 
              white-space: pre-wrap; 
              font-family: 'Monaco', 'Courier New', monospace;
              background: #f4f4f4;
              padding: 10px;
              border-radius: 4px;
              overflow-x: auto;
            }
          </style>
        </head>
        <body>
    `;

    // Add answer text (main narrative / markdown)
    const narrative = message.answer ?? message.content;
    if (narrative) {
      htmlContent += safeHtml`<h1>Response</h1><div>${escapeHtmlWithBreaks(narrative)}</div>`;
    }

    // Add structured response sections as readable HTML (no JSON)
    const printIntent =
      typeof message.responseMetadata?.intent_type === 'string'
        ? message.responseMetadata.intent_type
        : undefined;
    const structured = structuredPayloadForChartBriefingView(
      printIntent,
      message.responseMetadata?.structured as StructuredResponse | undefined,
    );
    if (structured?.sections && Array.isArray(structured.sections)) {
      if (structured.title) htmlContent += safeHtml`<h2>${structured.title}</h2>`;
      const lensLabels = (structured.meta as { specialty_lens_labels?: string[] } | undefined)
        ?.specialty_lens_labels;
      if (Array.isArray(lensLabels) && lensLabels.length > 0) {
        htmlContent += safeHtml`<p style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;color:#64748b;margin:10px 0 8px;">Clinical lenses</p>`;
        htmlContent += safeHtml`<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:12px;margin-bottom:12px;">`;
        lensLabels.forEach((rawLabel, gi) => {
          const branches = splitLensGroupBranches(String(rawLabel));
          const title =
            lensLabels.length > 1 ? `Specialty group ${gi + 1}` : 'Specialty group';
          htmlContent += safeHtml`<div style="border:1px solid #e2e8f0;border-radius:12px;padding:12px 12px 12px 14px;background:linear-gradient(145deg,#ffffff,#f8fafc);box-shadow:0 1px 2px rgba(15,23,42,0.06);border-left:4px solid #0ea5e9;">`;
          htmlContent += safeHtml`<div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;margin-bottom:8px;">${title}</div>`;
          htmlContent += safeHtml`<div style="display:flex;flex-wrap:wrap;gap:6px;">`;
          branches.forEach((b) => {
            htmlContent += safeHtml`<span style="display:inline-block;padding:5px 10px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;font-size:13px;font-weight:500;color:#0f172a;">${b}</span>`;
          });
          htmlContent += safeHtml`</div></div>`;
        });
        htmlContent += safeHtml`</div>`;
      } else if (structured.subtitle) {
        htmlContent += safeHtml`<p>${structured.subtitle}</p>`;
      }
      for (const section of structured.sections) {
        if (section.title) htmlContent += safeHtml`<h3>${section.title}</h3>`;
        if (section.type === 'paragraph' && section.content) {
          htmlContent += safeHtml`<p>${section.content}</p>`;
        } else if (section.type === 'table' && section.columns && section.rows) {
          // `rawHtml` is required on these three: the interpolated value is markup we
          // built ourselves with `safeHtml`, so it is already escaped — without the
          // marker the outer tag would escape the `<th>`/`<td>`/`<li>` tags too.
          htmlContent += safeHtml`<table><tr>${rawHtml(
            section.columns
              .map((c: any) => safeHtml`<th>${typeof c === 'string' ? c : c.label || c.key || ''}</th>`)
              .join(''),
          )}</tr>`;
          for (const row of section.rows) {
            htmlContent += safeHtml`<tr>${rawHtml(
              section.columns
                .map((c: any) => {
                  const key = typeof c === 'string' ? c : (c.key || c.label || '');
                  return safeHtml`<td>${row[key] ?? ''}</td>`;
                })
                .join(''),
            )}</tr>`;
          }
          htmlContent += safeHtml`</table>`;
        } else if ((section.type === 'list' || section.type === 'badges') && Array.isArray(section.items)) {
          htmlContent += safeHtml`<ul>${rawHtml(
            section.items
              .map((it: any) => safeHtml`<li>${typeof it === 'string' ? it : (it.label || it.name || it.text || '')}</li>`)
              .join(''),
          )}</ul>`;
        } else if (section.type === 'kv' && section.rows) {
          htmlContent += safeHtml`<table><tr><th>Field</th><th>Value</th></tr>`;
          for (const r of section.rows) {
            htmlContent += safeHtml`<tr><td><strong>${r.label || r.key || ''}</strong></td><td>${r.value ?? ''}</td></tr>`;
          }
          htmlContent += safeHtml`</table>`;
        }
      }
    }

    // Add widget content (tables, lists — no raw JSON)
    if (printIntent !== 'chart_briefing' && message.widget) {
      htmlContent += safeHtml`<div class="widget">`;
      htmlContent += widgetToHTML(message.widget);
      htmlContent += safeHtml`</div>`;
    }

    // Add suggestions
    if (message.suggestions) {
      htmlContent += safeHtml`<div style="margin: 20px 0; padding: 15px; background: #e3f2fd; border-left: 4px solid #2196f3; border-radius: 4px;">`;
      htmlContent += safeHtml`<h3>Next Steps</h3>`;
      htmlContent += safeHtml`<p>${message.suggestions}</p>`;
      htmlContent += safeHtml`</div>`;
    }

    // Footer with timestamp
    if (message.timestamp) {
      htmlContent += safeHtml`<div class="evidence" style="margin-top: 30px;"><p><small>Generated: ${new Date(message.timestamp).toLocaleString()}</small></p></div>`;
    }

    htmlContent += safeHtml`
        </body>
      </html>
    `;

    // Create a new window with the HTML content for printing/saving as PDF
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog (which allows saving as PDF)
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
      
      // Also set up a fallback timeout
      setTimeout(() => {
        if (printWindow && !printWindow.closed) {
          printWindow.print();
        }
      }, 1000);
    } else {
      // Fallback: download as HTML file (can be opened and saved as PDF)
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `message-${Date.now()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  const handleCopy = async () => {
    let content = '';
    
    // Add answer text
    const narrative = message.answer ?? message.content;
    if (narrative) {
      content += `${narrative}\n\n`;
    }

    // Add structured response text
    const copyIntent =
      typeof message.responseMetadata?.intent_type === 'string'
        ? message.responseMetadata.intent_type
        : undefined;
    const structuredData = structuredPayloadForChartBriefingView(
      copyIntent,
      message.responseMetadata?.structured as StructuredResponse | undefined,
    );
    if (structuredData?.sections && Array.isArray(structuredData.sections)) {
      for (const section of structuredData.sections) {
        if (section.title) content += `## ${section.title}\n`;
        if (section.type === 'paragraph' && section.content) {
          content += section.content + '\n\n';
        } else if (section.type === 'table' && section.columns && section.rows) {
          const cols = section.columns.map((c: any) => typeof c === 'string' ? c : (c.label || c.key || ''));
          content += `| ${cols.join(' | ')} |\n`;
          content += `| ${cols.map(() => '---').join(' | ')} |\n`;
          for (const row of section.rows) {
            content += `| ${cols.map((c: string) => row[c] ?? '').join(' | ')} |\n`;
          }
          content += '\n';
        } else if ((section.type === 'list' || section.type === 'badges') && section.items) {
          for (const it of section.items as Array<string | { label?: string; name?: string; text?: string }>) {
            const label = typeof it === 'string' ? it : (it.label || it.name || it.text || '');
            content += `• ${label}\n`;
          }
          content += '\n';
        } else if (section.type === 'kv' && section.rows) {
          for (const r of section.rows) {
            content += `${r.label || r.key || ''}: ${r.value ?? ''}\n`;
          }
          content += '\n';
        }
      }
    }
    
    // Add widget text
    if (copyIntent !== 'chart_briefing' && message.widget) {
      const widgetText = extractTextFromWidget(message.widget);
      if (widgetText) {
        content += widgetText + '\n\n';
      }
    }
    
    // If no content, try to extract from DOM
    if (!content.trim() && messageRef.current) {
      const textContent = messageRef.current.innerText || messageRef.current.textContent || '';
      if (textContent.trim()) {
        content = textContent;
      }
    }
    
    if (!content.trim()) {
      console.warn('No copyable content found');
      return;
    }
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(content.trim());
      } else {
        // Fallback for older browsers or restricted contexts
        const textArea = document.createElement('textarea');
        textArea.value = content.trim();
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(textArea);
        }
      }
      
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Still show the copied state briefly to indicate the attempt
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    }
  };

  const canCopy = hasCopyableContent(message);

  if (message.role === 'user') {
    const userContent = (message.content || '').trim();
    const saved = isPromptSaved?.(userContent) ?? false;
    return (
      <div
        data-testid="message-user"
        className="flex justify-end relative group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isHovered && userContent && (onSavePrompt || onRemoveSavedPrompt) && (
          <div className="absolute -top-2 right-0 flex items-center gap-0.5 bg-surface-raised border border-border rounded-lg px-1.5 py-1 z-10 shadow-lg">
            {saved ? (
              <button
                type="button"
                onClick={() => onRemoveSavedPrompt?.(userContent)}
                className="p-1.5 hover:bg-surface-hover rounded transition-colors text-primary"
                title="Remove from saved prompts"
                aria-label="Remove from saved prompts"
              >
                <Bookmark className="w-4 h-4 fill-current" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onSavePrompt?.(userContent.slice(0, 40) + (userContent.length > 40 ? '…' : ''), userContent)}
                className="p-1.5 hover:bg-surface-hover rounded transition-colors text-text-tertiary"
                title="Add to saved prompts"
                aria-label="Add to saved prompts"
              >
                <Bookmark className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        <div className="bg-primary text-primary-foreground rounded-md px-3.5 py-2.5 max-w-[min(100%,36rem)] border border-primary sm:px-4 sm:py-3">
          <div className="text-[14px] leading-relaxed sm:text-[15px]">{message.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="message-assistant"
      ref={messageRef}
      className="relative min-w-0 space-y-4 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Download/Copy buttons */}
      {isHovered && (
        <div className="absolute -top-1 right-0 flex items-center gap-0.5 bg-surface-raised/95 backdrop-blur-xs border border-border rounded-lg px-2 py-1.5 z-10 shadow-lg">
          {canCopy && (
            <button
              onClick={handleCopy}
              className="p-1.5 hover:bg-surface-hover rounded transition-colors text-text-tertiary"
              title={isCopied ? "Copied!" : "Copy to clipboard"}
            >
              {isCopied ? (
                <Check className="w-4 h-4 text-status-positive-text" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          )}
          <button
            onClick={handleDownload}
            className="p-1.5 hover:bg-slate-50 hover:bg-surface-hover rounded transition-colors text-text-tertiary"
            title="Download as PDF"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Per-message Thinking Panel — only shown when this message has thinking data */}
      {message.role === 'assistant' && hasThinkingData && !cardDerivations.isSpecialtyResponse && (
        <ThinkingPanel
          events={normalizedEvents}
          metrics={thinkingMetrics}
          status={executionStatus}
          isStreaming={!!message.isStreaming}
          thinkingComplete={!!message.thinkingComplete}
          streamPhase={message.streamPhase}
          warningCount={warningCount}
          toolsUsedCount={toolsUsedCount}
          toolsUsed={toolsUsed}
          currentStepName={currentStepName}
        />
      )}

      {/* Main message content card */}
      <div className="min-w-0 w-full bg-surface-raised rounded-md border border-border overflow-hidden">
        <div className="min-w-0 p-4 sm:p-6">
        {/* CMA-OPT-68/69/71: the live progress lines, inside the answer area —
            the place the backend used to type these same sentences into as
            plain answer text (USER ORDER 2026-08-13: one set of lines, each
            with its own spinner and check, and no second copy). Deliberately
            NOT behind the isSpecialtyResponse gate that hides the ThinkingPanel
            below: CMA responses ARE specialty responses, which is exactly why
            the panel never showed these lines. */}
        {message.role === 'assistant' && !!message.isStreaming && (
          <CmaProgressLines events={normalizedEvents} isStreaming={!!message.isStreaming} />
        )}

        {/* The skeleton bars stand in for an answer area with nothing in it.
            While the progress lines are showing, the area is not empty, so the
            bars would only add noise underneath them. */}
        {message.isStreaming && message.streamPhase === 'thinking' && !message.answer && cmaProgressLineCount === 0 && (
          <div className="space-y-3 py-1">
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <span className="thinking-pulse-dot" />
              <span className="font-medium">Analyzing your request…</span>
            </div>
            <div className="space-y-2 animate-pulse">
              <div className="h-3 bg-surface-sunken rounded-full w-3/4" />
              <div className="h-3 bg-surface-sunken rounded-full w-1/2" />
              <div className="h-3 bg-surface-sunken rounded-full w-5/6" />
            </div>
          </div>
        )}

        {message.isStreaming && message.streamPhase === 'final' && !message.answer && cmaProgressLineCount === 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              <span className="font-medium">Writing response…</span>
            </div>
            <div className="space-y-2 animate-pulse">
              <div className="h-3 bg-surface-sunken rounded-full w-5/6" />
              <div className="h-3 bg-surface-sunken rounded-full w-2/3" />
            </div>
          </div>
        )}
        {message.isStreaming && message.streamPhase === 'final' && message.answer && (
          <div className="mb-1 flex items-center gap-2 text-xs text-text-tertiary">
            <Loader2 className="w-3 h-3 animate-spin text-primary" />
            <span className="font-medium">Writing…</span>
          </div>
        )}

          {/* NEW-11: agent-drafted artifacts (draft_note / draft_plan /
              revise_note — the registered-tool contract, so record-fetching
              tools like get_care_plans never match) are unmistakably drafts. */}
          {message.role === 'assistant' && hasDraftArtifactTools(toolsUsed) && (
            <DraftAdvisoryStrip className="mb-4" />
          )}
          {/* Answer: prefer structured renderer, fall back to markdown */}
          <StructuredOrMarkdown message={message} answerScopeId={answerScopeId} />
          {!message.isStreaming &&
            shouldRenderChartBriefingFollowUpChips(message) &&
            onSendSuggestion && (
              <ChartBriefingFollowUpChips onSelect={onSendSuggestion} />
            )}
          {/* specialty-multi-agent renderer family (RouterOverviewPanel /
              SpecialtyDetailedResultsPanel) stays outside the unified
              ResponseFrame — legacy/out-of-scope for ticket #101 (WS-5). */}
          {!message.isStreaming && isSpecialtyResponse ? (
            <RouterOverviewPanel overview={effectiveRouterOverview} specialties={effectiveEngagedSpecialties} />
          ) : null}
          {!message.isStreaming && isSpecialtyResponse ? (
            <SpecialtyDetailedResultsPanel
              outputs={effectiveSpecialtyOutputs}
              skippedSpecialties={skippedSpecialtiesWithoutRag}
            />
          ) : null}
          <ResponseFrame
            references={
              // Same supersession as the other ResponseFrame render site
              // above -- the citation-free GuidelineEvidencePanel is the
              // current design and always wins over the legacy numbered one.
              !message.isStreaming && !suppressClinicalRefs && guidelineEvidence ? (
                <GuidelineEvidencePanel evidence={guidelineEvidence} />
              ) : !message.isStreaming &&
                !isSpecialtyResponse &&
                !hasSpecialtyOutputs &&
                !suppressClinicalRefs &&
                clinicalRefSources.length > 0 ? (
                <RagSourcesPanel sources={clinicalRefSources} hasInlineCitations={hasInlineCitationMarkers} />
              ) : null
            }
            chips={
              !message.isStreaming ? (
                <SuggestedQuestionsPanel
                  suggestions={suggestedNextQuestions}
                  onSelect={onSendSuggestion}
                />
              ) : null
            }
            footer={
              <>
          {!message.isStreaming &&
            message.responseMetadata &&
            clinicalQualityVisible(message.responseMetadata) && (
              <div className="mt-4">
                <ClinicalQualityReviewPanel
                  metadata={message.responseMetadata}
                  clinicalQualityRerunUserText={clinicalQualityRerunUserText}
                  onClinicalQualityRerun={onClinicalQualityRerun}
                />
              </div>
            )}
          {/* Export download button (CSV/PDF) */}
          {message.widget?.data?.filename && (message.widget.data.format === 'csv' || message.widget.data.format === 'pdf') && (
            <div className="mt-4">
              <a
                href={`${(import.meta.env.VITE_MARCH_API_URL ?? '').toString().replace(/\/$/, '')}/api/exports/${encodeURIComponent(String(message.widget.data.filename))}`}
                download={String(message.widget.data.filename)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-surface-sunken/30 hover:bg-surface-sunken/50 text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                {message.widget.data.format === 'pdf' ? 'Download PDF' : 'Download CSV'}
              </a>
            </div>
          )}

          {/* Clinical references render above the suggested questions (task #65);
              this footer block keeps only timing/flow chrome. */}
          {!message.isStreaming && message.responseMetadata && !isSpecialtyResponse && (
            <div className="mt-5 space-y-3">
              {/* CodeReferencesPanel removed — codes shown only as inline hover tooltips */}

              {/* Compact footer: timing + flow */}
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-[11px] text-text-tertiary/70 pt-2 border-t border-border/30">
              {(message.responseMetadata.client_processing_time_ms ?? message.responseMetadata.processing_time_ms) != null && (
                // #180: client-measured end-to-end (submit -> final metadata commit) is
                // the headline figure — it was found to diverge from the server's
                // self-reported processing_time_ms by 0.7-3.5s live. The server figure
                // stays visible as a title-attribute tooltip, never dropped.
                <div
                  className="flex items-center gap-1"
                  title={
                    message.responseMetadata.processing_time_ms != null
                      ? `server processing: ${formatDuration(message.responseMetadata.processing_time_ms)}`
                      : undefined
                  }
                >
                  <Clock className="w-3 h-3" />
                  <span>
                    {formatDuration(
                      (message.responseMetadata.client_processing_time_ms ??
                        message.responseMetadata.processing_time_ms) as number,
                    )}
                  </span>
                </div>
              )}
              {message.responseMetadata.execution_flow && (
                <>
                  <span className="text-border/30">·</span>
                  <span>{flowLabel(message.responseMetadata.execution_flow, message.responseMetadata.agentic_core_adk_response_contract, message.responseMetadata.intent_type)}</span>
                </>
              )}
              {!hasSpecialtyOutputs && clinicalRefSources.length > 0 && (
                <>
                  <span className="text-border/30">·</span>
                  <span className="text-brand-text font-medium">References</span>
                </>
              )}
              {message.responseMetadata.code_references && message.responseMetadata.code_references.length > 0 && (
                <>
                  <span className="text-border/30">·</span>
                  <span className="text-(--chart-categorical-1) font-medium">UMLS</span>
                </>
              )}
              {(message.responseMetadata.clinical_quality_gate_status === 'passed' ||
                (message.responseMetadata.clinical_quality_gate_satisfied === true &&
                  message.responseMetadata.clinical_quality_layer_authoritative === true)) && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-positive-text font-medium cursor-help">
                        <ShieldCheck className="w-3 h-3 shrink-0" />
                        Clinical quality checked
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left border border-border shadow-lg">
                      <p className="text-xs text-text-tertiary">
                        Terminal clinical-quality review passed (rubric floors met).
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {message.responseMetadata.clinical_quality_gate_status === 'provisional' && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-attention-text font-medium cursor-help">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        Provisional quality
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left border border-border shadow-lg">
                      <p className="text-xs text-text-tertiary">
                        Some rubric targets were not met; confirm details in the source record before clinical use.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {message.responseMetadata.clinical_quality_gate_status === 'failed' && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-negative-text font-medium cursor-help">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        Quality gate failed
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm text-left border border-border shadow-lg">
                      <p className="text-xs text-text-tertiary">
                        Hard safety or factuality floors were not met. Do not rely on this reply for care without
                        independent verification.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {message.responseMetadata.high_risk_user_prompt === true && (
                <>
                  <span className="text-border/30">·</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-status-attention-text font-medium cursor-help">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        Sensitive prompt
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm text-left border border-border shadow-lg">
                      <p className="text-[10px] uppercase tracking-wide text-text-tertiary mb-1.5">Scope policy</p>
                      <p className="text-xs leading-relaxed">
                        {(message.responseMetadata.high_risk_hits?.length
                          ? message.responseMetadata.high_risk_hits.join('; ')
                          : 'This user message matched high-caution patterns. Review the reply carefully.')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {/* Orchestrator answer_contract (e.g. missing_question_anchor) is not shown in the clinician
                  UI; use clinical quality gate / review panel as the only quality signal. */}
              </div>
            </div>
          )}
              </>
            }
          />

          {/* Legacy evidence (backward compat) */}
          {showEvidence && message.evidence && !message.responseMetadata && (
            <div className="text-[11px] sm:text-xs text-text-tertiary flex items-center gap-2 sm:gap-3 flex-wrap pb-2 border-b border-border mb-3">
            {message.evidence.sources && (
              <div className="flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5" />
                <span>{message.evidence.sources} sources</span>
              </div>
            )}
            {message.evidence.generated && (
              <>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Generated {String(message.evidence.generated)}</span>
                </div>
              </>
            )}
            {message.evidence.runId && (
              <>
                <span>•</span>
                <span className="font-mono text-[9px] sm:text-xs bg-surface-sunken px-2 py-1 rounded">ID: {String(message.evidence.runId).slice(0, 8)}</span>
              </>
            )}
            </div>
          )}

          {message.suggestions && (
            <div className="text-sm text-text-tertiary bg-primary/8 border border-primary/20 rounded-lg px-4 py-3 flex items-start gap-3">
              <Zap className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
              <span><span className="font-medium text-foreground/90">Next:</span> {message.suggestions}</span>
            </div>
          )}

          {message.retryCommand && onRetryCommand && (
            <div className="mt-4 pt-4 border-t border-border/50">
              <button
                type="button"
                onClick={() => onRetryCommand(message.retryCommand)}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-linear-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-xl transition-all shadow-sm hover:shadow-md"
              >
                <RotateCw className="w-4 h-4" />
                Retry Command
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

function formatStructuredData(widget: any): React.ReactNode {
  if (!widget?.data) return null;
  const d = widget.data;
  const get = (a: string, b: string) => d[a] ?? d[b];
  switch (widget.type) {
    case 'W_FHIR_PATIENT':
      if (d.found && (d.configured !== false)) {
        const items: string[] = [];
        if (get('name', 'name')) items.push(`**Name:** ${get('name', 'name')}`);
        if (get('birthDate', 'date_of_birth')) items.push(`**Birth date:** ${get('birthDate', 'date_of_birth')}`);
        if (get('gender', 'sex_at_birth')) items.push(`**Gender:** ${get('gender', 'sex_at_birth')}`);
        const age = d.age ?? d.ageYears ?? d.age_years;
        if (age != null) items.push(`**Age:** ${age}`);
        if (get('maritalStatus', 'marital_status_text')) items.push(`**Marital status:** ${get('maritalStatus', 'marital_status_text')}`);
        if (get('patientId', 'patient_id') || get('fhirId', 'fhir_id')) items.push(`**Patient ID:** ${get('patientId', 'patient_id') || get('fhirId', 'fhir_id')}`);
        if (items.length === 0) return null;
        return (
          <div className="text-sm text-text-tertiary space-y-1">
            {items.map((line, i) => (
              <div key={i}>
                {/* SEC-I1: `line` is model-generated. react-markdown only emits raw
                    HTML when rehype-raw is installed, which it is not — rehypeSanitize
                    makes that guarantee explicit so adding rehype-raw later cannot
                    silently open an injection path. */}
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{line}</ReactMarkdown>
              </div>
            ))}
          </div>
        );
      }
      return d.message ? <p className="text-sm text-text-tertiary">{d.message}</p> : null;
    case 'W_SUMMARY_CLINICAL': {
      const parts: string[] = [];
      if (typeof d.summary === 'string' && d.summary.trim()) {
        parts.push(String(d.summary).trim());
      }
      if (typeof d.details === 'string' && d.details.trim()) {
        const details = String(d.details).trim();
        parts.push(looksLikeMarkdown(details) ? details : `**Details**\n${details}`);
      }
      if (Array.isArray(d.keyPoints) && d.keyPoints.length > 0) {
        parts.push(`**Key Points**\n${d.keyPoints.map((p: unknown) => `- ${String(p)}`).join('\n')}`);
      }
      const text = parts.join('\n\n').trim();
      return text ? <MarkdownAnswer text={text} /> : null;
    }
    case 'W_MEDICATIONS':
    case 'W_ENCOUNTERS':
    case 'W_CONDITIONS':
    case 'W_ALLERGIES':
    case 'W_OBSERVATIONS':
    case 'W_PROCEDURES':
    case 'W_IMMUNIZATIONS':
    case 'W_REPORTS':
    case 'W_CAREPLANS':
      if (d.items && Array.isArray(d.items) && d.items.length > 0) {
        return (
          <ul className="list-disc list-inside space-y-1 text-sm text-foreground">
            {d.items.slice(0, 30).map((it: any, i: number) => {
              const label = it._display ?? it.name ?? it.vaccine_name ?? it.medication ?? it.type ?? it.display_name ?? it.substance ?? '—';
              const extra = it.dose ?? it.date ?? it.occurrence_date ?? it.start_date ?? it.authored_on ?? it.status ?? it.performed_date ?? it.effective_date;
              return (
                <li key={i}>
                  {typeof label === 'string' ? label : String(label)}
                  {extra && <span className="text-text-tertiary"> — {extra}</span>}
                </li>
              );
            })}
          </ul>
        );
      }
      return null;
    default: {
      const text = extractTextFromWidget(widget);
      const dd = widget.data;
      if (typeof dd === 'object' && dd !== null && !Array.isArray(dd)) {
        const narrativeMarkdown = buildNarrativeWidgetMarkdown(dd as Record<string, unknown>);
        if (narrativeMarkdown) {
          return <MarkdownAnswer text={narrativeMarkdown} />;
        }
      }
      if (typeof dd === 'object' && dd !== null && !Array.isArray(dd)) {
        const entries = Object.entries(dd).filter(([, v]) => v != null && v !== '');
        if (entries.length > 0) {
          return (
            <div className="rounded-lg border border-border/60 p-3 bg-surface-sunken/30 text-sm space-y-2">
              <div className="grid gap-x-4 gap-y-1 grid-cols-[auto_1fr]">
                {entries.slice(0, 12).map(([k, v], i) => (
                  <React.Fragment key={i}>
                    <span className="font-medium text-text-tertiary capitalize">
                      {String(k).replace(/_/g, ' ')}
                    </span>
                    <span className="text-foreground wrap-break-word">
                      {Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              {entries.length > 12 && (
                <p className="text-xs text-text-tertiary pt-1">
                  +{entries.length - 12} more fields
                </p>
              )}
            </div>
          );
        }
      }
      if (!text) return null;
      return looksLikeMarkdown(text) || text.includes('\n')
        ? <MarkdownAnswer text={text} />
        : <p className="text-sm text-text-tertiary whitespace-pre-wrap wrap-break-word">{text}</p>;
    }
  }
}
